import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import useIsMobile from '@/hooks/useIsMobile';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Tabs, Tab } from '@heroui/react';
import { Input, Textarea } from '@heroui/input';
import Edit from '@/icons/Edit';
import Trash from '@/icons/Trash';
import {
    fetchContacts,
    createContact,
    updateContact,
    deleteContact,
    bulkDeleteContacts,
    addContactsToList,
    removeContactsFromList,
    createListAndAddContacts,
    sendEmailToList,
    sendNotificationToList,
    fetchContactLists,
    createContactList,
    updateContactList,
    deleteContactList,
    fetchContactListById,
    fetchAllTags,
    fetchAllLists,
    uploadEmailImage,
    bulkImportContactsFromCSV,
    updateContactListForm,
    getContactListSubmissions,
    downloadContactListAppleWalletPass,
    downloadContactListGoogleWalletPass,
    fetchOrganizerById,
    getEmailRequestsByListId,
    getEmailRecipients,
    getActiveEmailJobs,
    retryFailedEmails
} from '@/apiEndpoints';
import EditIcon from '@/icons/EditIcon';
import FormGenerator, { type FormData, type FormQuestion, type FormGeneratorRef } from './FormGenerator';
import QRCode from 'qrcode';
import { addToast } from '@heroui/toast';
import { isAppleWalletSupported, isGoogleWalletSupported } from '@/utils/appleWallet';
import PrintIcon from '@/icons/PrintIcon';
import DownloadIcon from '@/icons/DownloadIcon';
import CopyIcon from '@/icons/CopyIcon';
import AddToWalletApple from '@/icons/AddToWalletApple';
import AddToWalletGoogle from '@/icons/AddToWalletGoogle';
import TextFieldIcon from '@/icons/TextFieldIcon';
import TextAreaIcon from '@/icons/TextAreaIcon';
import CheckboxIcon from '@/icons/CheckboxIcon';
import SelectIcon from '@/icons/SelectIcon';
import { card, surface, Segmented, input as inputBase, label as labelCls } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText, label: 'font-semibold text-grey20 dark:text-nightTextSecondary' };
const textareaCls = { inputWrapper: `py-3 ${fieldWrap}`, input: fieldText, label: 'font-semibold text-grey20 dark:text-nightTextSecondary' };
const modalCls = {
    base: 'rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-nightSurface dark:ring-white/10',
    header: 'px-6 pt-6 text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white',
    body: 'px-6 text-grey20 dark:text-nightTextSecondary',
    footer: 'gap-2 px-6 pb-6',
    closeButton: 'right-4 top-4 rounded-full text-grey10 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10',
    backdrop: 'bg-black/50',
};
const nativeField = 'w-full rounded-2xl bg-grey99 px-4 py-3 text-sm text-grey10 ring-1 ring-black/[0.04] transition-shadow placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60';
const pill = 'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold';
const thCls = 'px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.12em] text-grey50 dark:text-nightMuted';
const rowCls = 'border-t border-black/[0.06] transition-colors hover:bg-grey99 dark:border-white/10 dark:hover:bg-white/[0.04]';
const cellText = 'text-sm font-medium text-grey10 dark:text-white';
const iconBtn = 'flex h-9 w-9 items-center justify-center rounded-full bg-grey99 text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10';
const iconBtnDanger = 'flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20';
const stickyBg = 'bg-white dark:bg-[#121212]';
const thStick = `sticky top-0 border-b border-black/[0.06] px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.12em] text-grey50 dark:border-white/10 dark:text-nightMuted ${stickyBg}`;
const tdCls = 'border-b border-black/[0.06] px-4 py-3 align-middle transition-colors group-hover:bg-grey99 dark:border-white/10 dark:group-hover:bg-white/[0.04]';
const tdStick = `sticky border-b border-black/[0.06] px-4 py-3 align-middle transition-colors group-hover:bg-grey99 dark:border-white/10 dark:group-hover:bg-[#1b1b1b] ${stickyBg}`;
const skel = 'animate-pulse rounded-full bg-grey95 dark:bg-white/10';
const checkboxCls = 'h-4 w-4 rounded border-grey80 text-primary focus:ring-primary/40 dark:border-white/20 dark:accent-nightAccent';
const toolBtn = 'h-10 rounded-full bg-transparent px-4 text-sm font-semibold text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]';
const accentBtnSm = 'h-10 rounded-full bg-primary px-5 text-sm font-semibold text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg';
const sectionHeading = 'text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white';

interface Contact {
    _id: string;
    name: string;
    lastName: string;
    email: string;
    phone: string;
    tags: string[];
    listId?: string;
    formData?: { [key: string]: any };
    createdAt: string;
    updatedAt: string;
}

interface ContactList {
    _id: string;
    name: string;
    description?: string;
    contactCount: number;
    createdAt: string;
    updatedAt: string;
    formTitle?: string;
    formDescription?: string;
    formQuestions?: FormQuestion[];
    isFormActive?: boolean;
    organizationId?: string;
}

interface ContactListResponse {
    data: Contact[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

interface ContactListListResponse {
    data: ContactList[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

const ContactListTab: React.FC = () => {
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const { selectedOrganizerId } = useOrganization();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedList, setSelectedList] = useState('all');
    const [selectedTag, setSelectedTag] = useState('all');
    const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
    const navigate = useNavigate();
    // Modal disclosure hooks
    const { isOpen: isAddContactOpen, onOpen: onAddContactOpen, onClose: onAddContactClose } = useDisclosure();
    const { isOpen: isEditContactOpen, onOpen: onEditContactOpen, onClose: onEditContactClose } = useDisclosure();
    const { isOpen: isNewListOpen, onOpen: onNewListOpen, onClose: onNewListClose } = useDisclosure();
    const { isOpen: isAddToListOpen, onOpen: onAddToListOpen, onClose: onAddToListClose } = useDisclosure();
    const { isOpen: isRemoveFromListOpen, onOpen: onRemoveFromListOpen, onClose: onRemoveFromListClose } = useDisclosure();
    const { isOpen: isSendEmailOpen, onOpen: onSendEmailOpen, onClose: onSendEmailClose } = useDisclosure();
    const { isOpen: isSendNotificationOpen, onOpen: onSendNotificationOpen, onClose: onSendNotificationClose } = useDisclosure();
    const { isOpen: isManageListsOpen, onOpen: onManageListsOpen, onClose: onManageListsClose } = useDisclosure();
    const { isOpen: isEditListOpen, onOpen: onEditListOpen, onClose: onEditListClose } = useDisclosure();
    const { isOpen: isImportCsvOpen, onOpen: onImportCsvOpen, onClose: onImportCsvClose } = useDisclosure();
    const { isOpen: isExportCsvOpen, onOpen: onExportCsvOpen, onClose: onExportCsvClose } = useDisclosure();
    const { isOpen: isFormBuilderOpen, onOpen: onFormBuilderOpen, onClose: onFormBuilderClose } = useDisclosure();
    const { isOpen: isFormSubmissionsOpen, onOpen: onFormSubmissionsOpen, onClose: onFormSubmissionsClose } = useDisclosure();
    const { isOpen: isDeleteListConfirmOpen, onOpen: onDeleteListConfirmOpen, onClose: onDeleteListConfirmClose } = useDisclosure();
    const { isOpen: isBulkDeleteConfirmOpen, onOpen: onBulkDeleteConfirmOpen, onClose: onBulkDeleteConfirmClose } = useDisclosure();
    const { isOpen: isDeleteContactConfirmOpen, onOpen: onDeleteContactConfirmOpen, onClose: onDeleteContactConfirmClose } = useDisclosure();
    const { isOpen: isEmailRecipientsOpen, onOpen: onEmailRecipientsOpen, onClose: onEmailRecipientsClose } = useDisclosure();
    const { isOpen: isEmailPreviewOpen, onOpen: onEmailPreviewOpen, onClose: onEmailPreviewClose } = useDisclosure();
    const { isOpen: isEmailHistoryPreviewOpen, onOpen: onEmailHistoryPreviewOpen, onClose: onEmailHistoryPreviewClose } = useDisclosure();
    const [selectedEmailForPreview, setSelectedEmailForPreview] = useState<any>(null);
    const { isOpen: isImageSettingsOpen, onOpen: onImageSettingsOpen, onClose: onImageSettingsClose } = useDisclosure();
    const [selectedImage, setSelectedImage] = useState<{ element: HTMLImageElement; editorRef: React.RefObject<HTMLDivElement | null> } | null>(null);
    const [currentImageAlignment, setCurrentImageAlignment] = useState<'left' | 'center' | 'right'>('left');
    const [imageLinkUrl, setImageLinkUrl] = useState<string>('');

    const [editingList, setEditingList] = useState<ContactList | null>(null);
    const [listToDelete, setListToDelete] = useState<{ id: string; name: string } | null>(null);
    const [contactToDelete, setContactToDelete] = useState<{ id: string; name: string } | null>(null);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedListForForm, setSelectedListForForm] = useState<ContactList | null>(null);
    const [selectedListForSubmissions, setSelectedListForSubmissions] = useState<ContactList | null>(null);
    // View state: null = list view, ContactList = detail view
    // Initialize tab from URL params on mount/refresh
    const tabFromUrl = searchParams.get('tab') || 'contacts';
    const listIdFromUrl = searchParams.get('listId');
    const [selectedListView, setSelectedListView] = useState<ContactList | null>(null);
    const [activeTab, setActiveTab] = useState<string>(tabFromUrl);
    const hasInitializedFromUrlRef = useRef(false);
    const [isInitializingFromUrl, setIsInitializingFromUrl] = useState(!!listIdFromUrl);
    const [selectedListForExport, setSelectedListForExport] = useState<string>('');
    const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);
    const [isSavingForm, setIsSavingForm] = useState<boolean>(false);
    const [selectedEmailRequest, setSelectedEmailRequest] = useState<string | null>(null);

    // New contact form state
    const [newContact, setNewContact] = useState({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        tags: [] as string[],
        listId: undefined as string | undefined
    });
    const [selectedListForNewContact, setSelectedListForNewContact] = useState<string>('');
    const [selectedListFormQuestions, setSelectedListFormQuestions] = useState<FormQuestion[]>([]);
    const [formQuestionAnswers, setFormQuestionAnswers] = useState<{ [questionId: string]: string | string[] }>({});
    const [emailError, setEmailError] = useState<string>('');
    const [isLoadingFormQuestions, setIsLoadingFormQuestions] = useState<boolean>(false);

    // Edit contact form questions state
    const [editContactFormQuestions, setEditContactFormQuestions] = useState<FormQuestion[]>([]);
    const [editContactFormAnswers, setEditContactFormAnswers] = useState<{ [questionId: string]: string | string[] }>({});
    const [editEmailError, setEditEmailError] = useState<string>('');
    const [isLoadingEditFormQuestions, setIsLoadingEditFormQuestions] = useState<boolean>(false);

    // Email validation function
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // New list form state
    const [newList, setNewList] = useState({
        name: '',
        description: '',
        organizationId: selectedOrganizerId || undefined
    });

    // Add to list form state
    const [addToList, setAddToList] = useState('');
    const [showNewListInAdd, setShowNewListInAdd] = useState(false);
    const [newListInAdd, setNewListInAdd] = useState({
        name: '',
        description: '',
        organizationId: selectedOrganizerId || undefined
    });

    // Remove from list form state
    const [removeFromList, setRemoveFromList] = useState('');

    // Send email form state
    const [emailData, setEmailData] = useState({
        subject: '',
        message: '',
        listId: '',
        emailName: '',
        replyToEmail: ''
    });

    // Email validation state
    const [replyToEmailError, setReplyToEmailError] = useState<string>('');

    // Send notification form state
    const [notificationData, setNotificationData] = useState({
        title: '',
        message: '',
        listId: ''
    });

    useEffect(() => {
        if (selectedListView) {
            setSelectedListView(null);
            navigate('/organizations/events');
        }
    }, [selectedOrganizerId]);

    // Edit list form state
    const [editList, setEditList] = useState({
        name: '',
        description: '',
        organizationId: selectedOrganizerId || undefined
    });

    // Edit contact form state
    const [editContact, setEditContact] = useState({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        tags: [] as string[],
        listId: undefined as string | undefined
    });

    // CSV import/export state
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvPreview, setCsvPreview] = useState<any[]>([]);
    const [csvImportError, setCsvImportError] = useState<string>('');
    const [csvImportListId, setCsvImportListId] = useState<string>('');
    const [isCsvImporting, setIsCsvImporting] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const emailImageInputRef = useRef<HTMLInputElement>(null);
    const notificationImageInputRef = useRef<HTMLInputElement>(null);
    const formGeneratorRef = useRef<FormGeneratorRef>(null);

    // Custom email editor ref
    const emailEditorRef = useRef<HTMLDivElement>(null);
    const [emailEditorState, setEmailEditorState] = useState({
        isBold: false,
        isItalic: false,
        isStrike: false,
        fontSize: '16px',
        fontColor: '#000000',
        linkUrl: '',
        imageAlign: 'left'
    });

    // Custom notification editor ref
    const notificationEditorRef = useRef<HTMLDivElement>(null);
    const [notificationEditorState, setNotificationEditorState] = useState({
        isBold: false,
        isItalic: false,
        isStrike: false,
        fontSize: '16px',
        fontColor: '#000000',
        linkUrl: '',
        imageAlign: 'left'
    });

    // Update editor content when email data changes
    useEffect(() => {
        if (emailEditorRef.current && emailData.message !== emailEditorRef.current.innerHTML) {
            emailEditorRef.current.innerHTML = emailData.message;
        }
    }, [emailData.message]);

    // Initialize email editor when modal opens
    useEffect(() => {
        if (isSendEmailOpen && emailEditorRef.current) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                if (emailEditorRef.current && emailData.message) {
                    emailEditorRef.current.innerHTML = emailData.message;
                }
            }, 100);
        }
    }, [isSendEmailOpen]);

    // Update editor content when notification data changes
    useEffect(() => {
        if (notificationEditorRef.current && notificationData.message !== notificationEditorRef.current.innerHTML) {
            notificationEditorRef.current.innerHTML = notificationData.message;
        }
    }, [notificationData.message]);

    // Initialize notification editor when modal opens
    useEffect(() => {
        if (isSendNotificationOpen && notificationEditorRef.current) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                if (notificationEditorRef.current && notificationData.message) {
                    notificationEditorRef.current.innerHTML = notificationData.message;
                }
            }, 100);
        }
    }, [isSendNotificationOpen]);

    // Update email data when editor content changes
    const handleEmailEditorChange = () => {
        if (emailEditorRef.current) {
            setEmailData(prev => ({ ...prev, message: emailEditorRef.current?.innerHTML || '' }));
        }
    };

    // Update notification data when editor content changes
    const handleNotificationEditorChange = () => {
        if (notificationEditorRef.current) {
            setNotificationData(prev => ({ ...prev, message: notificationEditorRef.current?.innerHTML || '' }));
        }
    };

    // Custom editor command functions
    const execCommand = (command: string, value?: string) => {
        document.execCommand(command, false, value);
        if (emailEditorRef.current) {
            emailEditorRef.current.focus();
            handleEmailEditorChange();
        }
    };

    const execNotificationCommand = (command: string, value?: string) => {
        document.execCommand(command, false, value);
        if (notificationEditorRef.current) {
            notificationEditorRef.current.focus();
            handleNotificationEditorChange();
        }
    };

    // Check current formatting state
    const checkFormatting = (editorRef: React.RefObject<HTMLDivElement | null>) => {
        if (!editorRef.current) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const isBold = document.queryCommandState('bold');
        const isItalic = document.queryCommandState('italic');
        const isStrike = document.queryCommandState('strikeThrough');

        // Get font size and color from computed style
        const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentElement
            : range.commonAncestorContainer as Element;

        if (container) {
            const computedStyle = window.getComputedStyle(container);
            const fontSize = computedStyle.fontSize || '16px';
            const fontColor = computedStyle.color || '#000000';

            if (editorRef === emailEditorRef) {
                setEmailEditorState(prev => ({
                    ...prev,
                    isBold,
                    isItalic,
                    isStrike,
                    fontSize,
                    fontColor: rgbToHex(fontColor)
                }));
            } else {
                setNotificationEditorState(prev => ({
                    ...prev,
                    isBold,
                    isItalic,
                    isStrike,
                    fontSize,
                    fontColor: rgbToHex(fontColor)
                }));
            }
        }
    };

    // Convert RGB to hex
    const rgbToHex = (rgb: string): string => {
        if (rgb.startsWith('#')) return rgb;
        const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
        if (match) {
            const r = parseInt(match[1], 10).toString(16).padStart(2, '0');
            const g = parseInt(match[2], 10).toString(16).padStart(2, '0');
            const b = parseInt(match[3], 10).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        }
        return '#000000';
    };

    // Insert link
    const insertLink = (editorRef: React.RefObject<HTMLDivElement | null>, url?: string) => {
        if (!editorRef.current) return;
        editorRef.current.focus();

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const selectedText = selection.toString().trim();

        if (url) {
            // If URL provided, create link
            if (selectedText) {
                document.execCommand('createLink', false, url);
            } else {
                // Insert link with text
                const link = document.createElement('a');
                link.href = url;
                link.textContent = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.style.color = '#2563eb';
                link.style.textDecoration = 'underline';
                range.insertNode(link);
                range.setStartAfter(link);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        } else {
            // Check if selection is already a link
            let container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                ? range.commonAncestorContainer.parentElement
                : range.commonAncestorContainer as Element;

            const linkElement = container?.closest('a') as HTMLAnchorElement;
            if (linkElement) {
                const currentUrl = linkElement.getAttribute('href') || '';
                const newUrl = prompt('Enter URL:', currentUrl);
                if (newUrl) {
                    linkElement.setAttribute('href', newUrl);
                    linkElement.setAttribute('target', '_blank');
                    linkElement.setAttribute('rel', 'noopener noreferrer');
                    linkElement.style.color = '#2563eb';
                    linkElement.style.textDecoration = 'underline';
                }
            } else {
                const newUrl = prompt('Enter URL:');
                if (newUrl) {
                    if (selectedText) {
                        document.execCommand('createLink', false, newUrl);
                        // Set target and rel for the created link, and apply styling
                        setTimeout(() => {
                            const links = editorRef.current?.querySelectorAll('a[href="' + newUrl + '"]');
                            links?.forEach(link => {
                                const anchor = link as HTMLAnchorElement;
                                anchor.setAttribute('target', '_blank');
                                anchor.setAttribute('rel', 'noopener noreferrer');
                                anchor.style.color = '#2563eb';
                                anchor.style.textDecoration = 'underline';
                            });
                        }, 10);
                    } else {
                        // Insert link with URL as text
                        const link = document.createElement('a');
                        link.href = newUrl;
                        link.textContent = newUrl;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        link.style.color = '#2563eb';
                        link.style.textDecoration = 'underline';
                        range.insertNode(link);
                        range.setStartAfter(link);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }
            }
        }

        // Apply link styling to all links in editor
        setTimeout(() => {
            const links = editorRef.current?.querySelectorAll('a');
            links?.forEach(link => {
                const anchor = link as HTMLAnchorElement;
                if (!anchor.style.color) {
                    anchor.style.color = '#2563eb';
                    anchor.style.textDecoration = 'underline';
                }
            });
        }, 10);

        if (editorRef === emailEditorRef) {
            handleEmailEditorChange();
        } else {
            handleNotificationEditorChange();
        }
    };

    // Set font size
    const setFontSize = (size: string, editorRef: React.RefObject<HTMLDivElement | null>) => {
        if (!editorRef.current) return;
        editorRef.current.focus();

        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            // If selection is collapsed (cursor only), apply to the current block
            if (range.collapsed) {
                const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                    ? range.commonAncestorContainer.parentElement
                    : range.commonAncestorContainer as Element;

                if (container) {
                    const span = document.createElement('span');
                    span.style.fontSize = size;
                    const textNode = document.createTextNode('\u200B'); // Zero-width space
                    span.appendChild(textNode);
                    range.insertNode(span);
                    range.setStartAfter(span);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            } else {
                // If there's a selection, wrap it in a span
                try {
                    const span = document.createElement('span');
                    span.style.fontSize = size;
                    range.surroundContents(span);
                } catch (e) {
                    // If surroundContents fails, extract and wrap
                    const contents = range.extractContents();
                    const span = document.createElement('span');
                    span.style.fontSize = size;
                    span.appendChild(contents);
                    range.insertNode(span);
                }
            }
        } else {
            // Fallback: use execCommand with styleWithCSS
            document.execCommand('styleWithCSS', false, 'true');
            document.execCommand('fontSize', false, '7');
            // Then find and replace the font tag with span
            setTimeout(() => {
                if (editorRef.current) {
                    const fontTags = editorRef.current.querySelectorAll('font[size="7"]');
                    fontTags.forEach(font => {
                        const span = document.createElement('span');
                        span.style.fontSize = size;
                        span.innerHTML = font.innerHTML;
                        font.parentNode?.replaceChild(span, font);
                    });
                }
            }, 10);
        }

        if (editorRef === emailEditorRef) {
            handleEmailEditorChange();
        } else {
            handleNotificationEditorChange();
        }
    };

    // Set font color
    const setFontColor = (color: string, editorRef: React.RefObject<HTMLDivElement | null>) => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        document.execCommand('foreColor', false, color);

        if (editorRef === emailEditorRef) {
            handleEmailEditorChange();
        } else {
            handleNotificationEditorChange();
        }
    };

    // Align image - updated to work with specific image element
    const alignImage = (align: 'left' | 'center' | 'right', imgElement?: HTMLImageElement) => {
        const img = imgElement || selectedImage?.element;
        if (!img) return;

        // Check if image is already wrapped in a container div
        let container = img.parentElement;
        const isWrapped = container &&
            (container.tagName === 'DIV') &&
            (container.style.width === '100%' || container.getAttribute('data-image-wrapper') === 'true');

        // If not wrapped, wrap it
        if (!isWrapped) {
            // Create wrapper div
            const wrapper = document.createElement('div');
            wrapper.setAttribute('data-image-wrapper', 'true');
            wrapper.style.width = '100%';
            wrapper.style.margin = '10px 0';
            wrapper.style.clear = 'both';
            wrapper.style.overflow = 'hidden';

            // Insert wrapper before image
            img.parentNode?.insertBefore(wrapper, img);
            // Move image into wrapper
            wrapper.appendChild(img);
            container = wrapper;
        }

        // Remove float styles from image
        img.style.float = 'none';
        img.style.display = 'inline-block';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.margin = '0';
        img.style.verticalAlign = 'middle';

        // Set alignment on container
        if (container) {
            container.style.textAlign = align;
            container.style.width = '100%';
            container.style.clear = 'both';
            container.style.overflow = 'hidden';
            container.setAttribute('data-image-wrapper', 'true');
        }

        const editorRef = selectedImage?.editorRef;
        if (editorRef === emailEditorRef) {
            setEmailEditorState(prev => ({ ...prev, imageAlign: align }));
            handleEmailEditorChange();
        } else if (editorRef === notificationEditorRef) {
            setNotificationEditorState(prev => ({ ...prev, imageAlign: align }));
            handleNotificationEditorChange();
        }
    };

    // Get image link URL
    const getImageLink = (img: HTMLImageElement): string => {
        // Check if image is wrapped in an anchor tag
        const parent = img.parentElement;
        if (parent && parent.tagName === 'A') {
            return (parent as HTMLAnchorElement).href || '';
        }
        // Check if image has onclick or data-link attribute
        return img.getAttribute('data-link') || '';
    };

    // Set image link
    const setImageLink = (img: HTMLImageElement, url: string) => {
        if (!url || url.trim() === '') {
            // Remove link if URL is empty
            const parent = img.parentElement;
            if (parent && parent.tagName === 'A') {
                const grandParent = parent.parentElement;
                if (grandParent) {
                    grandParent.insertBefore(img, parent);
                    grandParent.removeChild(parent);
                } else {
                    img.parentNode?.replaceChild(img, parent);
                }
            }
            img.removeAttribute('data-link');
            return;
        }

        // Ensure URL has protocol
        let linkUrl = url.trim();
        if (!linkUrl.startsWith('http://') && !linkUrl.startsWith('https://') && !linkUrl.startsWith('mailto:')) {
            linkUrl = 'https://' + linkUrl;
        }

        // Check if image is already wrapped in an anchor
        const parent = img.parentElement;
        if (parent && parent.tagName === 'A') {
            (parent as HTMLAnchorElement).href = linkUrl;
            (parent as HTMLAnchorElement).target = '_blank';
            (parent as HTMLAnchorElement).rel = 'noopener noreferrer';
        } else {
            // Wrap image in anchor tag
            const anchor = document.createElement('a');
            anchor.href = linkUrl;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            anchor.style.textDecoration = 'none';
            anchor.style.display = 'inline-block';

            // Insert anchor before image
            img.parentNode?.insertBefore(anchor, img);
            // Move image into anchor
            anchor.appendChild(img);
        }
        img.setAttribute('data-link', linkUrl);
    };

    // Handle image click to open settings modal or open link
    const handleImageClick = (event: MouseEvent, editorRef: React.RefObject<HTMLDivElement | null>) => {
        const target = event.target as HTMLElement;
        if (target.tagName === 'IMG') {
            const img = target as HTMLImageElement;
            const linkUrl = getImageLink(img);

            // If Ctrl/Cmd is pressed and image has a link, open the link
            if ((event.ctrlKey || event.metaKey) && linkUrl) {
                window.open(linkUrl, '_blank', 'noopener,noreferrer');
                return;
            }

            // Otherwise open settings modal
            event.preventDefault();
            event.stopPropagation();
            const alignment = getImageAlignment(img);
            setSelectedImage({ element: img, editorRef });
            setCurrentImageAlignment(alignment);
            setImageLinkUrl(linkUrl);
            onImageSettingsOpen();
        }
    };

    // Get current image alignment
    const getImageAlignment = (img: HTMLImageElement): 'left' | 'center' | 'right' => {
        // Check parent container's text-align first
        const container = img.parentElement;
        if (container) {
            const containerTextAlign = container.style.textAlign || window.getComputedStyle(container).textAlign;
            if (containerTextAlign === 'left') return 'left';
            if (containerTextAlign === 'right') return 'right';
            if (containerTextAlign === 'center') return 'center';
        }

        // Fallback to image's own styles
        const float = img.style.float || window.getComputedStyle(img).float;
        const textAlign = img.style.textAlign || window.getComputedStyle(img).textAlign;
        const margin = img.style.margin || window.getComputedStyle(img).margin;

        if (float === 'left') return 'left';
        if (float === 'right') return 'right';
        if (margin === '0px auto' || margin.includes('auto') || textAlign === 'center') return 'center';
        return 'left'; // default
    };

    // Setup image click handlers and styling, and link styling
    useEffect(() => {
        const emailEditor = emailEditorRef.current;
        const notificationEditor = notificationEditorRef.current;

        const emailClickHandler = (e: MouseEvent) => handleImageClick(e, emailEditorRef);
        const notificationClickHandler = (e: MouseEvent) => handleImageClick(e, notificationEditorRef);

        // Style images to be clickable
        const styleImages = (editor: HTMLDivElement | null) => {
            if (!editor) return;
            const images = editor.querySelectorAll('img');
            images.forEach(img => {
                img.style.cursor = 'pointer';
                img.style.border = '2px solid transparent';
                img.style.transition = 'border-color 0.2s';
                img.style.borderRadius = '4px';
                img.addEventListener('mouseenter', () => {
                    img.style.borderColor = '#3b82f6';
                });
                img.addEventListener('mouseleave', () => {
                    img.style.borderColor = 'transparent';
                });
            });
        };

        // Style links to have different colors
        const styleLinks = (editor: HTMLDivElement | null) => {
            if (!editor) return;
            const links = editor.querySelectorAll('a');
            links.forEach(link => {
                if (!link.style.color) {
                    link.style.color = '#2563eb';
                    link.style.textDecoration = 'underline';
                }
                link.addEventListener('mouseenter', () => {
                    link.style.color = '#1d4ed8';
                });
                link.addEventListener('mouseleave', () => {
                    link.style.color = '#2563eb';
                });
            });
        };

        if (emailEditor) {
            emailEditor.addEventListener('click', emailClickHandler);
            styleImages(emailEditor);
            styleLinks(emailEditor);
            // Re-style images and links when content changes
            const observer = new MutationObserver(() => {
                styleImages(emailEditor);
                styleLinks(emailEditor);
            });
            observer.observe(emailEditor, { childList: true, subtree: true });
        }
        if (notificationEditor) {
            notificationEditor.addEventListener('click', notificationClickHandler);
            styleImages(notificationEditor);
            styleLinks(notificationEditor);
            // Re-style images and links when content changes
            const observer = new MutationObserver(() => {
                styleImages(notificationEditor);
                styleLinks(notificationEditor);
            });
            observer.observe(notificationEditor, { childList: true, subtree: true });
        }

        return () => {
            if (emailEditor) {
                emailEditor.removeEventListener('click', emailClickHandler);
            }
            if (notificationEditor) {
                notificationEditor.removeEventListener('click', notificationClickHandler);
            }
        };
    }, [isSendEmailOpen, isSendNotificationOpen]);

    // Set listId when send email modal opens from detail view
    useEffect(() => {
        if (isSendEmailOpen && selectedListView && !emailData.listId) {
            setEmailData(prev => ({ ...prev, listId: selectedListView._id }));
        }
    }, [isSendEmailOpen, selectedListView]);

    // Update organizationId when selectedOrganizerId changes
    useEffect(() => {
        setNewList(prev => ({ ...prev, organizationId: selectedOrganizerId || undefined }));
        setNewListInAdd(prev => ({ ...prev, organizationId: selectedOrganizerId || undefined }));
        // Only update editList if not currently editing (to avoid overwriting user changes)
        if (!editingList) {
            setEditList(prev => ({ ...prev, organizationId: selectedOrganizerId || undefined }));
        }
    }, [selectedOrganizerId, editingList]);

    // Set listId when export CSV modal opens from detail view
    useEffect(() => {
        if (isExportCsvOpen && selectedListView && !selectedListForExport) {
            setSelectedListForExport(selectedListView._id);
        }
    }, [isExportCsvOpen, selectedListView]);

    // Image functions using server upload
    const handleImageUpload = async (editorRef: React.RefObject<HTMLDivElement | null>, file: File) => {
        if (file && file.type.startsWith('image/')) {
            try {
                // Upload image to server
                const result = await uploadEmailImage(file);

                // Insert image with server URL
                if (editorRef.current) {
                    editorRef.current.focus();

                    // Create wrapper div for proper alignment
                    const wrapper = document.createElement('div');
                    wrapper.setAttribute('data-image-wrapper', 'true');
                    wrapper.style.width = '100%';
                    wrapper.style.margin = '10px 0';
                    wrapper.style.textAlign = 'left';
                    wrapper.style.clear = 'both';
                    wrapper.style.overflow = 'hidden';

                    // Create image element
                    const img = document.createElement('img');
                    img.src = result.imageUrl;
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    img.style.borderRadius = '8px';
                    img.style.display = 'inline-block';
                    img.style.margin = '0';
                    img.style.verticalAlign = 'middle';

                    // Add image to wrapper
                    wrapper.appendChild(img);

                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        range.insertNode(wrapper);
                        range.collapse(false);
                    } else {
                        editorRef.current.appendChild(wrapper);
                    }

                    if (editorRef === emailEditorRef) {
                        handleEmailEditorChange();
                    } else {
                        handleNotificationEditorChange();
                    }
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                alert('Failed to upload image. Please try again.');
            }
        }
    };

    const handleEmailImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && emailEditorRef.current) {
            await handleImageUpload(emailEditorRef, file);
        }
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    };

    const handleNotificationImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && notificationEditorRef.current) {
            await handleImageUpload(notificationEditorRef, file);
        }
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    };

    // Fetch organizer data for email preview
    const { data: organizerData } = useQuery({
        queryKey: ['organizer', selectedOrganizerId],
        queryFn: async () => {
            if (!selectedOrganizerId) return null;
            return await fetchOrganizerById(selectedOrganizerId);
        },
        enabled: !!selectedOrganizerId,
    });

    // Fetch organizer data for email history preview
    const { data: historyOrganizerData } = useQuery({
        queryKey: ['organizer', selectedEmailForPreview?.organizationId],
        queryFn: async () => {
            if (!selectedEmailForPreview?.organizationId) return null;
            return await fetchOrganizerById(selectedEmailForPreview.organizationId);
        },
        enabled: !!selectedEmailForPreview?.organizationId,
    });

    // NOTE: Keep this in sync with backend template: tpm-api/email_template_contactlist_new.html
    const CONTACTLIST_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <title>Contact Confirmation - Eventup</title>
    <!--[if mso]>
    <style type="text/css">
        body, table, td {font-family: Arial, sans-serif !important;}
    </style>
    <![endif]-->
</head>

<body
    style="margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #E6F2F5; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;">
    <div style=" background-color: #E6F2F5; padding: 40px 20px;">
        <div style="max-width: 600px; margin: 0 auto;">
            <!-- Header - Evnetup Logo -->
            <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                    <td align="center" style="padding: 40px 0 30px 0;">
                        <a href="https://eventup.ai" style="display: inline-block; text-decoration: none;">
                            <img src="https://eventup.ai/logoColor.png"
                                style="height:40px;width:160px;vertical-align:middle;margin-left:4px;object-fit: contain;" />
                        </a>
                    </td>
                </tr>
            </table>

            <!-- Main Content Box -->
            <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                    <td>
                        <div
                            style="background-color: #FFFFFF; border-radius: 8px; padding: 30px 40px 40px; position: relative;">
                            <!-- Organization Section -->
                            <div style="position: relative; padding-bottom: 0px; margin-bottom: 0px;">
                                <table width="100%" border="0" cellspacing="0" cellpadding="0"
                                    style="border-collapse: collapse;">
                                    <tr>
                                        <td width="60" style="vertical-align: top; padding-right: 16px;">
                                            <div
                                                style="width: 104px; height: 104px; border-radius: 100%; background-color: #FFFFFF; box-shadow: 0px 6px 44px 0px #0000000D; display: block; position: relative; margin-left: 0px; overflow: hidden;">
                                                <img src="{{logo}}"
                                                    style="width:100%;height:100%;object-fit: contain;" />
                                            </div>
                                        </td>
                                        <td style="vertical-align: middle;">
                                            <a href="{{url}}"
                                                style="font-size: 14px; font-weight: 500; text-decoration: none; display: inline-block; ">
                                                <div
                                                    style="color: #465050; font-size: 24px; font-weight: 600; line-height: 1.4;">
                                                    {{title}}</div>
                                            </a>
                                        </td>
                                    </tr>
                                </table>

                                <!-- Dashed Separator -->
                                <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin: 10px 0;">
                                    <tr>
                                        <td>
                                            <div
                                                style="border-top: 1px dashed #D5DADB; margin: 10px 0 10px 0; width: 100%;">
                                            </div>
                                        </td>
                                    </tr>
                                </table>
                            </div>

                            <!-- Message Body -->
                            <div style="position: relative;">
                                <div
                                    style="font-size: 16px; font-weight: 700; color: #2D3748; margin: 0 0 16px 0; line-height: 1.5;">
                                    {{name}}</div>
                                <div style="font-size: 15px; color: #4A5568; line-height: 1.7; margin: 0 0 20px 0;">
                                    {{content}}
                                </div>
                                <div style="font-size: 16px; font-weight: 700; color: #2D3748; margin: 20px 0 8px 0;">
                                    Thank you!</div>
                                <div style="font-size: 15px; color: #4A5568; margin: 8px 0 0 0;">{{title2}} Support Team
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </table>

            <!-- Footer -->
            <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                    <td align="center" style="padding: 40px 0 30px 0;">
                        <!-- Footer Logo -->
                        <div style="margin-bottom: 24px;">
                            <a href="https://eventup.ai" style="display: inline-block; text-decoration: none;">
                                <img src="https://eventup.ai/logoColor.png"
                                    style="height:23px;width:93px;vertical-align:middle;margin-left:4px;object-fit: contain;" />
                            </a>
                        </div>

                        <!-- Navigation Links -->
                        <div style="margin-bottom: 24px;">
                            <a href="https://eventup.ai"
                                style="color: #005E64 !important; font-size: 14px; font-weight: 400; text-decoration: none;">Eventup.ai</a>
                            <span style="color: #2D3748; margin: 0 8px;">|</span>
                            <a href="mailto:admin@nexalogix.com"
                                style="color: #005E64 !important; font-size: 14px; font-weight: 400; text-decoration: none;">Contact</a>
                            <span style="color: #2D3748; margin: 0 8px;">|</span>
                            <a href="https://eventup.ai/privacy-policy/"
                                style="color: #005E64 !important; font-size: 14px; font-weight: 400; text-decoration: none;">Privacy</a>
                            <span style="color: #2D3748; margin: 0 8px;">|</span>
                            <a href="https://eventup.ai/terms-conditions/"
                                style="color: #005E64 !important; font-size: 14px; font-weight: 400; text-decoration: none;">Terms</a>
                        </div>

                        <!-- Social Media Icons -->
                        <div style="margin-bottom: 24px;">
                            <a href="https://www.instagram.com/eventup.ai/"
                                style="width: 32px; height: 32px; border-radius: 4px; display: inline-block; text-decoration: none; margin: 0 8px; vertical-align: middle; position: relative; text-align: center; line-height: 32px;"
                                aria-label="Instagram">
                                <img src="https://eventup.ai/instagram.png"
                                    style="width:100%;height:100%;object-fit: contain;" />
                            </a>
                        </div>

                        <!-- Copyright -->
                        <div style="color: #465050; font-size: 12px; font-weight: 400; margin-top: 0px;">© 2025 Eventup.
                            All rights reserved</div>
                    </td>
                </tr>
            </table>
        </div>
    </div>
</body>

</html>`;

    // Generate email preview HTML
    const generateEmailPreview = () => {
        const template = CONTACTLIST_EMAIL_TEMPLATE;

        // Get logo from attachments or avatar field
        const logo = organizerData?.attachments?.avatar?.link || organizerData?.avatar || 'https://eventup.ai/logo-icon.png';
        // Get organization name
        const title = organizerData?.orgName || organizerData?.title || organizerData?.name || 'Eventup';
        const title2 = title;
        const name = 'Hello there,';
        const content = emailData.message || '<p>Your email content will appear here.</p>';
        // Keep preview URL consistent with backend: `${FRONTEND_URL}/organization/:id`
        const url = selectedOrganizerId ? `${window.location.origin}/organization/${selectedOrganizerId}` : '#';

        return template
            .replace(/\{\{logo\}\}/g, logo)
            .replace(/\{\{title\}\}/g, title)
            .replace(/\{\{title2\}\}/g, title2)
            .replace(/\{\{name\}\}/g, name)
            .replace(/\{\{content\}\}/g, content)
            .replace(/\{\{url\}\}/g, url);
    };

    // Generate email preview from email request data
    const generateEmailHistoryPreview = (request: any) => {
        const template = CONTACTLIST_EMAIL_TEMPLATE;

        // Get logo from attachments or avatar field, use historyOrganizerData if available, otherwise fallback to current organizerData
        const orgData = historyOrganizerData || organizerData;
        const logo = orgData?.attachments?.avatar?.link || orgData?.avatar || 'https://eventup.ai/logo-icon.png';
        // Get organization name
        const title = orgData?.orgName || orgData?.title || orgData?.name || 'Eventup';
        const title2 = title;
        const name = 'Hello there,';
        const content = request?.message || '<p>No message content available.</p>';
        const url = request?.organizationId ? `${window.location.origin}/organization/${request.organizationId}` : '#';

        return template
            .replace(/\{\{logo\}\}/g, logo)
            .replace(/\{\{title\}\}/g, title)
            .replace(/\{\{title2\}\}/g, title2)
            .replace(/\{\{name\}\}/g, name)
            .replace(/\{\{content\}\}/g, content)
            .replace(/\{\{url\}\}/g, url);
    };

    // Fetch contacts with React Query
    // When in detail view, use selectedListView, otherwise use selectedList filter
    const listFilter = selectedListView?._id || (selectedList !== 'all' ? selectedList : undefined);
    const {
        data: contactsData,
        isFetching: isContactsLoading,
        isError: isContactsError,
        error: contactsError
    } = useQuery({
        queryKey: ['contacts', currentPage, searchTerm, listFilter, selectedTag, rowsPerPage],
        queryFn: () => fetchContacts({
            page: currentPage,
            limit: selectedListView ? rowsPerPage : 10,
            search: searchTerm || undefined,
            list: listFilter,
            tag: selectedTag !== 'all' ? selectedTag : undefined
        }),
        enabled: !!user?.userId && !!selectedListView?._id
    });

    // Fetch contact lists
    const {
        data: contactListsData,
        isFetching: isContactListsLoading,
        refetch: refetchContactLists
    } = useQuery({
        queryKey: ['contactLists'],
        queryFn: () => fetchContactLists({ page: 1, limit: 100 }),
        enabled: !!user?.userId
    });

    // Fetch all tags
    /* const {
        data: tagsData
    } = useQuery({
        queryKey: ['allTags'],
        queryFn: fetchAllTags,
        enabled: !!user?.userId
    });  */

    // Fetch all lists
    /*   const {
          data: listsData
      } = useQuery({
          queryKey: ['allLists'],
          queryFn: fetchAllLists,
          enabled: !!user?.userId
      }); */

    // Fetch email requests for selected list
    const {
        data: emailRequestsData,
        isFetching: isEmailRequestsLoading,
        refetch: refetchEmailRequests
    } = useQuery({
        queryKey: ['emailRequests', selectedListView?._id],
        queryFn: () => getEmailRequestsByListId(selectedListView!._id),
        enabled: !!user?.userId && !!selectedListView?._id && activeTab === 'emailHistory',
        refetchInterval: 60000, // Refetch every 1 minute (60000 milliseconds)
    });

    // Fetch email recipients for selected request
    const {
        data: emailRecipientsData,
        isFetching: isEmailRecipientsLoading,
        refetch: refetchEmailRecipients
    } = useQuery({
        queryKey: ['emailRecipients', selectedEmailRequest],
        queryFn: () => getEmailRecipients(selectedEmailRequest!),
        enabled: !!user?.userId && !!selectedEmailRequest && isEmailRecipientsOpen,
        refetchInterval: 60000, // Refetch every 1 minute (60000 milliseconds)
    });

    // Fetch active email jobs with progress (polling every 10 seconds if there are active jobs)
    const {
        data: activeEmailJobs,
        isFetching: isActiveJobsLoading,
        refetch: refetchActiveEmailJobs
    } = useQuery({
        queryKey: ['activeEmailJobs', selectedOrganizerId],
        queryFn: () => getActiveEmailJobs(selectedOrganizerId || ''),
        enabled: !!user?.userId && !!selectedOrganizerId && !!selectedListView,
    });

    // Set up polling: refetch every 10 seconds if there are active jobs
    useEffect(() => {
        const hasActiveJobs = activeEmailJobs && Array.isArray(activeEmailJobs) && activeEmailJobs.length > 0;

        if (!hasActiveJobs) {
            return; // Stop polling if no active jobs
        }

        // Set up interval to refetch every 10 seconds
        const intervalId = setInterval(() => {
            refetchActiveEmailJobs();
        }, 10000); // 10 seconds

        // Cleanup interval on unmount or when activeEmailJobs changes
        return () => {
            clearInterval(intervalId);
        };
    }, [activeEmailJobs, refetchActiveEmailJobs]);

    // Extract data from queries
    const contacts = contactsData?.data || [];
    const totalPages = contactsData?.totalPages || 1;
    const totalContacts = contactsData?.total || 1;
    const contactLists = contactListsData?.data || [];
    const emailRequests = emailRequestsData?.requests || [];
    const emailRecipients = emailRecipientsData?.recipients || [];
    /* const allTags = tagsData?.tags || [];
    const allLists = listsData?.lists || []; */

    // Create contact mutation
    const createContactMutation = useMutation({
        mutationFn: createContact,
        onSuccess: () => {
            // Invalidate all related queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['allTags'] });
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            queryClient.invalidateQueries({ queryKey: ['allLists'] });

            onAddContactClose();
            setNewContact({ name: '', lastName: '', email: '', phone: '', tags: [], listId: undefined });
            setSelectedListForNewContact('');
            setSelectedListFormQuestions([]);
            setFormQuestionAnswers({});
            setEmailError('');
            setIsLoadingFormQuestions(false);
        },
        onError: (error) => {
            console.error('Error creating contact:', error);
        }
    });

    // Fetch form questions when a list is selected
    useEffect(() => {
        const loadFormQuestions = async () => {
            if (!selectedListForNewContact) {
                setSelectedListFormQuestions([]);
                setFormQuestionAnswers({});
                setIsLoadingFormQuestions(false);
                return;
            }

            setIsLoadingFormQuestions(true);
            try {
                const list = await fetchContactListById(selectedListForNewContact);
                if (list && list.formQuestions && list.formQuestions.length > 0) {
                    setSelectedListFormQuestions(list.formQuestions);
                } else {
                    setSelectedListFormQuestions([]);
                    setFormQuestionAnswers({});
                }
            } catch (error) {
                console.error('Error loading form questions:', error);
                setSelectedListFormQuestions([]);
                setFormQuestionAnswers({});
            } finally {
                setIsLoadingFormQuestions(false);
            }
        };

        loadFormQuestions();
    }, [selectedListForNewContact]);

    const handleFormQuestionChange = (questionId: string, value: string | string[]) => {
        setFormQuestionAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const renderFormQuestion = (question: FormQuestion) => {
        const answer = formQuestionAnswers[question.id];

        return (
            <div key={question.id} className="space-y-2">
                <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">
                        {question.title}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                </div>

                {question.description && (
                    <p className="text-xs text-grey50 dark:text-nightMuted">{question.description}</p>
                )}

                {question.type === 'short-answer' && (
                    <Input
                        classNames={inputCls}
                        placeholder="Your answer"
                        value={(answer as string) || ''}
                        onChange={(e) => handleFormQuestionChange(question.id, e.target.value)}
                        className="w-full"
                    />
                )}

                {question.type === 'long-answer' && (
                    <Textarea
                        classNames={textareaCls}
                        placeholder="Your answer"
                        value={(answer as string) || ''}
                        onChange={(e) => handleFormQuestionChange(question.id, e.target.value)}
                        className="w-full"
                        rows={4}
                    />
                )}

                {question.type === 'select' && (
                    <Select aria-label="Select" selectedKeys={[String(((answer as string) || '') ?? '')]} onChange={(e) => handleFormQuestionChange(question.id, e.target.value)}>
                        <SelectItem key="">Select an option</SelectItem>
                        {question.options?.map((option) => (                            <SelectItem key={String(option)}>{option}</SelectItem>
                        ))}
                    </Select>
                )}

                {question.type === 'checkbox' && (
                    <div className="space-y-2">
                        {question.options?.map((option, index) => {
                            const selectedOptions = (answer as string[]) || [];
                            const isChecked = selectedOptions.includes(option);

                            return (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id={`${question.id}_${index}`}
                                        checked={isChecked}
                                        onChange={(e) => {
                                            const currentOptions = (answer as string[]) || [];
                                            if (e.target.checked) {
                                                handleFormQuestionChange(question.id, [...currentOptions, option]);
                                            } else {
                                                handleFormQuestionChange(question.id, currentOptions.filter(opt => opt !== option));
                                            }
                                        }}
                                        className={checkboxCls}
                                    />
                                    <label htmlFor={`${question.id}_${index}`} className="text-sm text-grey20 dark:text-nightTextSecondary">
                                        {option}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const email = e.target.value;
        setNewContact(prev => ({ ...prev, email }));

        // Clear error when user starts typing
        if (emailError) {
            setEmailError('');
        }
    };

    const handleEmailBlur = () => {
        if (newContact.email && !validateEmail(newContact.email)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    const handleAddContact = async () => {
        if (!selectedListForNewContact) {
            alert('Please select a list for the contact');
            return;
        }

        // Validate email before submitting
        if (!newContact.email || !validateEmail(newContact.email)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        const contactData: any = {
            ...newContact,
            listId: selectedListForNewContact || undefined
        };

        // Include formData if there are form questions and answers
        if (selectedListFormQuestions.length > 0 && Object.keys(formQuestionAnswers).length > 0) {
            contactData.formData = formQuestionAnswers;
        }

        createContactMutation.mutate(contactData);
    };

    // Update contact mutation
    const updateContactMutation = useMutation({
        mutationFn: ({ id, data }: { id: string, data: any }) =>
            updateContact(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['allTags'] });
            onEditContactClose();
            setEditingContact(null);
            setEditContact({ name: '', lastName: '', email: '', phone: '', tags: [], listId: undefined });
            setEditContactFormQuestions([]);
            setEditContactFormAnswers({});
            setEditEmailError('');
            setIsLoadingEditFormQuestions(false);
        },
        onError: (error) => {
            console.error('Error updating contact:', error);
        }
    });

    // Create contact list mutation
    const createContactListMutation = useMutation({
        mutationFn: createContactList,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            queryClient.invalidateQueries({ queryKey: ['allLists'] });
            onNewListClose();
            setNewList({ name: '', description: '', organizationId: selectedOrganizerId || undefined });
        },
        onError: (error) => {
            console.error('Error creating contact list:', error);
        }
    });

    const handleCreateList = async () => {
        createContactListMutation.mutate(newList);
    };

    // Add contacts to list mutation
    const addContactsToListMutation = useMutation({
        mutationFn: ({ contactIds, listId }: { contactIds: string[], listId: string }) =>
            addContactsToList(contactIds, listId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            onAddToListClose();
            setSelectedContacts([]);
            setAddToList('');
        },
        onError: (error) => {
            console.error('Error adding contacts to list:', error);
        }
    });

    const handleAddToList = async () => {
        if (!addToList || selectedContacts.length === 0) return;
        addContactsToListMutation.mutate({ contactIds: selectedContacts, listId: addToList });
    };

    // Create new list and add contacts to it
    const createListAndAddContactsMutation = useMutation({
        mutationFn: ({ listData, contactIds }: { listData: any, contactIds: string[] }) =>
            createListAndAddContacts(listData, contactIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            queryClient.invalidateQueries({ queryKey: ['allLists'] });
            onAddToListClose();
            setSelectedContacts([]);
            setAddToList('');
            setShowNewListInAdd(false);
            setNewListInAdd({ name: '', description: '', organizationId: selectedOrganizerId || undefined });
        },
        onError: (error) => {
            console.error('Error creating list and adding contacts:', error);
        }
    });

    const handleCreateListAndAdd = async () => {
        if (!newListInAdd.name || selectedContacts.length === 0) return;
        createListAndAddContactsMutation.mutate({
            listData: newListInAdd,
            contactIds: selectedContacts
        });
    };

    // Remove contacts from list mutation
    const removeContactsFromListMutation = useMutation({
        mutationFn: ({ contactIds, listId }: { contactIds: string[], listId: string }) =>
            removeContactsFromList(contactIds, listId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            onRemoveFromListClose();
            setSelectedContacts([]);
            setRemoveFromList('');
        },
        onError: (error) => {
            console.error('Error removing contacts from list:', error);
        }
    });

    const handleRemoveFromList = async () => {
        const listIdToUse = selectedListView?._id || removeFromList;
        if (!listIdToUse || selectedContacts.length === 0) return;
        removeContactsFromListMutation.mutate({ contactIds: selectedContacts, listId: listIdToUse });
    };

    // Send email to list mutation
    // Retry failed emails mutation
    const retryFailedEmailsMutation = useMutation({
        mutationFn: async (requestId: string) => {
            return retryFailedEmails(requestId);
        },
        onSuccess: async (data) => {
            queryClient.invalidateQueries({ queryKey: ['emailRequests', selectedListView?._id] });
            queryClient.invalidateQueries({ queryKey: ['emailRecipients', selectedEmailRequest] });
            queryClient.invalidateQueries({ queryKey: ['activeEmailJobs', selectedOrganizerId] });
            // Also refetch active jobs directly
            if (refetchActiveEmailJobs) {
                await refetchActiveEmailJobs();
            }
            addToast({
                title: 'Success',
                description: data.message || `Retried ${data.retriedCount} failed emails`,
                color: 'success'
            });
        },
        onError: (error: any) => {
            console.error('Error retrying failed emails:', error);
            addToast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to retry failed emails. Please try again.',
                color: 'danger'
            });
        }
    });

    const sendEmailToListMutation = useMutation({
        mutationFn: ({ subject, message, listId, messageFile, emailName, replyToEmail }: {
            subject: string,
            message?: string,
            listId: string,
            messageFile?: File,
            emailName?: string,
            replyToEmail?: string
        }) => {
            return sendEmailToList(listId, subject, message || '', messageFile, emailName, replyToEmail);
        },
        onSuccess: (result) => {
            onSendEmailClose();
            setEmailData({ subject: '', message: '', listId: '', emailName: '', replyToEmail: '' });
            setReplyToEmailError(''); // Clear error on success
            //alert(`Email sent successfully to ${result.emailsSent} contacts in the list!`);
        },
        onError: (error) => {
            console.error('Error sending email:', error);
            alert('Failed to send email. Please try again.');
        }
    });

    const handleSendEmail = async () => {
        const listIdToUse = emailData.listId || selectedListView?._id;
        if (!emailData.subject || !emailData.message || !listIdToUse) return;

        // Validate reply-to email if provided
        if (emailData.replyToEmail && !validateEmail(emailData.replyToEmail)) {
            setReplyToEmailError('Please enter a valid email address');
            return;
        }

        // Create a file from the message content
        const messageFile = new File([emailData.message], 'email-content.html', {
            type: 'text/html'
        });

        // Send email with file directly using current mutation
        const result = await sendEmailToListMutation.mutateAsync({
            subject: emailData.subject,
            listId: listIdToUse,
            messageFile: messageFile,
            emailName: emailData.emailName || undefined,
            replyToEmail: emailData.replyToEmail || undefined
        });

        if (result) {
            refetchActiveEmailJobs()
        }
    };

    // Send notification to list mutation
    const sendNotificationToListMutation = useMutation({
        mutationFn: ({ title, message, listId }: { title: string, message: string, listId: string }) =>
            sendNotificationToList(listId, title, message),
        onSuccess: (result) => {
            onSendNotificationClose();
            setNotificationData({ title: '', message: '', listId: '' });
            alert(`Notification sent successfully to ${result.notificationsSent} contacts in the list!`);
        },
        onError: (error) => {
            console.error('Error sending notification:', error);
            alert('Failed to send notification. Please try again.');
        }
    });

    const handleSendNotification = async () => {
        if (!notificationData.title || !notificationData.message || !notificationData.listId) return;
        sendNotificationToListMutation.mutate(notificationData);
    };

    // Delete contact mutation
    const deleteContactMutation = useMutation({
        mutationFn: deleteContact,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
        },
        onError: (error) => {
            console.error('Error deleting contact:', error);
        }
    });

    const handleDeleteContact = async (contactId: string) => {
        const contact = contacts.find((c: Contact) => c._id === contactId);
        if (contact) {
            setContactToDelete({
                id: contactId,
                name: `${contact.name} ${contact.lastName}`.trim() || contact.email || 'this contact'
            });
            onDeleteContactConfirmOpen();
        }
    };

    const confirmDeleteContact = () => {
        if (!contactToDelete) return;
        deleteContactMutation.mutate(contactToDelete.id, {
            onSuccess: () => {
                setContactToDelete(null);
                onDeleteContactConfirmClose();
            }
        });
    };

    // Bulk delete contacts mutation
    const bulkDeleteContactsMutation = useMutation({
        mutationFn: bulkDeleteContacts,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            setSelectedContacts([]);
        },
        onError: (error) => {
            console.error('Error bulk deleting contacts:', error);
        }
    });

    const handleBulkDelete = async () => {
        if (selectedContacts.length === 0) return;
        onBulkDeleteConfirmOpen();
    };

    const confirmBulkDelete = () => {
        if (selectedContacts.length === 0) return;
        bulkDeleteContactsMutation.mutate(selectedContacts, {
            onSuccess: () => {
                setSelectedContacts([]);
                onBulkDeleteConfirmClose();
            }
        });
    };

    // Update contact list mutation
    const updateContactListMutation = useMutation({
        mutationFn: ({ id, data }: { id: string, data: any }) =>
            updateContactList(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            queryClient.invalidateQueries({ queryKey: ['allLists'] });
            onEditListClose();
            setEditingList(null);
            setEditList({ name: '', description: '', organizationId: selectedOrganizerId || undefined });
        },
        onError: (error) => {
            console.error('Error updating contact list:', error);
        }
    });

    // Delete contact list mutation
    const deleteContactListMutation = useMutation({
        mutationFn: (id: string) => deleteContactList(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            queryClient.invalidateQueries({ queryKey: ['allLists'] });
        },
        onError: (error) => {
            console.error('Error deleting contact list:', error);
        }
    });

    // Update contact list form mutation
    const updateContactListFormMutation = useMutation({
        mutationFn: ({ id, formData }: { id: string, formData: any }) =>
            updateContactListForm(id, formData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            onFormBuilderClose();
            // Update selectedListView if it matches the updated list
            if (selectedListView && selectedListView._id === variables.id) {
                queryClient.invalidateQueries({ queryKey: ['contactLists'] }).then(() => {
                    // The query will refetch and we'll update selectedListView in a useEffect
                });
            }
        },
        onError: (error) => {
            console.error('Error updating contact list form:', error);
        }
    });

    // Fetch form submissions query
    const { data: submissionsData, isLoading: isLoadingSubmissions, error: submissionsError } = useQuery({
        queryKey: ['contactListSubmissions', selectedListForSubmissions?._id],
        queryFn: async () => {
            if (!selectedListForSubmissions?._id) return null;
            return await getContactListSubmissions(selectedListForSubmissions._id);
        },
        enabled: !!selectedListForSubmissions?._id && isFormSubmissionsOpen,
        staleTime: 30000, // Cache for 30 seconds
    });

    const handleSelectContact = (contactId: string) => {
        setSelectedContacts(prev =>
            prev.includes(contactId)
                ? prev.filter(id => id !== contactId)
                : [...prev, contactId]
        );
    };

    const handleSelectAll = () => {
        if (selectedContacts.length === contacts.length) {
            setSelectedContacts([]);
        } else {
            setSelectedContacts(contacts.map(contact => contact._id));
        }
    };

    const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
            const tag = e.currentTarget.value.trim();
            if (!newContact.tags.includes(tag)) {
                setNewContact(prev => ({
                    ...prev,
                    tags: [...prev.tags, tag]
                }));
            }
            e.currentTarget.value = '';
        }
    };

    const removeTag = (tagToRemove: string) => {
        setNewContact(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    // List management handlers
    const handleEditList = (list: ContactList) => {
        setEditingList(list);
        setEditList({
            name: list.name,
            description: list.description || '',
            organizationId: list.organizationId || selectedOrganizerId || undefined
        });
        onEditListOpen();
    };

    const handleUpdateList = async () => {
        if (!editingList) return;
        updateContactListMutation.mutate({
            id: editingList._id,
            data: editList
        });
    };

    const handleDeleteList = (listId: string, listName: string) => {
        setListToDelete({ id: listId, name: listName });
        onDeleteListConfirmOpen();
    };

    const confirmDeleteList = () => {
        if (!listToDelete) return;
        deleteContactListMutation.mutate(listToDelete.id, {
            onSuccess: () => {
                // If deleting the currently selected list, go back to list view
                if (selectedListView && selectedListView._id === listToDelete.id) {
                    setSelectedListView(null);
                }
                onDeleteListConfirmClose();
                setListToDelete(null);
            }
        });
    };

    // Form builder handlers
    const handleOpenFormBuilder = (list: ContactList) => {
        setSelectedListForForm(list);
        onFormBuilderOpen();
    };

    const handleSaveForm = (formData: FormData) => {
        const listToUpdate = selectedListForForm || selectedListView;
        if (!listToUpdate) return;

        updateContactListFormMutation.mutate({
            id: listToUpdate._id,
            formData: {
                formTitle: formData.title,
                formDescription: formData.description,
                formQuestions: formData.questions,
                isFormActive: true
            }
        });
    };

    const handleOpenFormSubmissions = (list: ContactList) => {
        setSelectedListForSubmissions(list);
        onFormSubmissionsOpen();
    };

    // Generate QR code for contact list form
    const generateQRCode = (listId: string, id = null, retryCount = 0) => {
        const formUrl = `${window.location.origin}/contact-form/${id ?? listId}`;
        const canvas = document.getElementById(`qr-${listId}`) as HTMLCanvasElement;

        console.log(`Attempting to generate QR code for list ${listId}, retry: ${retryCount}`);
        console.log(`Form URL: ${formUrl}`);
        console.log(`Canvas found:`, !!canvas);
        console.log(`Canvas element:`, canvas);

        // Debug: List all canvas elements with qr- prefix
        const allQRCanvases = document.querySelectorAll('[id^="qr-"]');
        console.log(`All QR canvas elements found:`, allQRCanvases.length, Array.from(allQRCanvases).map(c => c.id));

        if (canvas) {
            console.log(`Canvas dimensions: ${canvas.width}x${canvas.height}`);
            QRCode.toCanvas(canvas, formUrl, {
                width: 84,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            }, (error) => {
                if (error) {
                    console.error('Error generating QR code:', error);
                } else {
                    console.log(`QR code generated successfully for list ${listId}`);
                }
            });
        } else if (retryCount < 5) {
            console.log(`Canvas not found for ${listId}, retrying...`);
            // Retry if canvas not found (DOM might not be ready)
            setTimeout(() => generateQRCode(listId, id, retryCount + 1), 200);
        } else {
            console.error(`Failed to find canvas for QR code after ${retryCount} retries: qr-${listId}`);
            console.error(`Available elements with id containing 'qr':`, document.querySelectorAll('[id*="qr"]'));
        }
    };

    // Generate QR codes for lists with forms
    useEffect(() => {
        if (contactListsData?.data) {
            console.log('Contact lists data loaded:', contactListsData.data.length, 'lists');

            // Use a longer delay to ensure DOM is ready
            setTimeout(() => {
                contactListsData.data.forEach((list: ContactList) => {
                    console.log(`Checking list ${list.name}:`, {
                        hasFormQuestions: !!(list.formQuestions && list.formQuestions.length > 0),
                        questionCount: list.formQuestions?.length || 0,
                        isFormActive: list.isFormActive,
                        shouldGenerateQR: !!(list.formQuestions && list.formQuestions.length > 0 && list.isFormActive)
                    });

                    if (list.formQuestions && list.formQuestions.length > 0 && list.isFormActive) {
                        generateQRCode(list._id);
                    }
                });
            }, 300);
        }
    }, [contactListsData]);

    // Generate QR code for form builder modal
    useEffect(() => {
        if (selectedListForForm && isFormBuilderOpen) {
            console.log('Generating QR code for form builder modal:', selectedListForForm._id);
            setTimeout(() => {
                generateQRCode(`modal-${selectedListForForm._id}`, selectedListView._id);
            }, 500);
        }
    }, [selectedListForForm, isFormBuilderOpen]);

    // Generate QR code for form builder tab
    useEffect(() => {
        if (selectedListView && activeTab === 'formBuilder') {
            console.log('Generating QR code for form builder tab:', selectedListView._id);
            setTimeout(() => {
                generateQRCode(`tab-${selectedListView._id}`, selectedListView._id);
            }, 500);
        }
    }, [selectedListView, activeTab]);

    // Initialize from URL params on mount/refresh - always prioritize URL params
    useEffect(() => {
        // If no listId in URL, we're not initializing from URL, so set flag immediately
        if (!listIdFromUrl && isInitializingFromUrl) {
            setIsInitializingFromUrl(false);
        }

        if (contactLists.length > 0 && !hasInitializedFromUrlRef.current) {
            const currentListIdFromUrl = searchParams.get('listId');
            const currentTabFromUrl = searchParams.get('tab') || 'contacts';

            // If URL has listId, find and set the list (force initialization from URL on refresh)
            if (currentListIdFromUrl) {
                const listFromUrl = contactLists.find(list => list._id === currentListIdFromUrl);
                if (listFromUrl) {
                    setSelectedListView(listFromUrl);
                    setActiveTab(currentTabFromUrl);
                    hasInitializedFromUrlRef.current = true;
                    setIsInitializingFromUrl(false);
                } else {
                    // List not found, clear URL params
                    const params = new URLSearchParams(searchParams);
                    params.delete('listId');
                    params.delete('tab');
                    setSearchParams(params, { replace: true });
                    hasInitializedFromUrlRef.current = true;
                    setIsInitializingFromUrl(false);
                }
            } else {
                // No listId in URL, ensure we're in list view
                if (selectedListView) {
                    setSelectedListView(null);
                }
                setActiveTab('contacts');
                hasInitializedFromUrlRef.current = true;
                setIsInitializingFromUrl(false);
            }
        }
    }, [contactLists, searchParams, selectedListView, setSearchParams, listIdFromUrl, isInitializingFromUrl]);

    // Update selectedListView if it exists and contactLists data changed (after initial load)
    useEffect(() => {
        if (hasInitializedFromUrlRef.current && selectedListView && contactLists.length > 0) {
            const updatedList = contactLists.find(list => list._id === selectedListView._id);
            if (updatedList && updatedList !== selectedListView) {
                setSelectedListView(updatedList);
            }
        }
    }, [contactLists, selectedListView?._id]);

    // Update URL when selectedListView or activeTab changes (but not during initial load from URL)
    useEffect(() => {
        if (!hasInitializedFromUrlRef.current) {
            return; // Don't update URL during initial load
        }

        const params = new URLSearchParams(searchParams);

        if (selectedListView) {
            params.set('listId', selectedListView._id);
            params.set('tab', activeTab);
        } else {
            params.delete('listId');
            params.delete('tab');
        }

        // Only update URL if it's different to avoid infinite loops
        const newUrl = params.toString();
        const currentUrl = searchParams.toString();
        if (newUrl !== currentUrl) {
            setSearchParams(params, { replace: true });
        }
    }, [selectedListView, activeTab, searchParams, setSearchParams]);

    // Reload data when tab changes
    useEffect(() => {
        if (selectedListView) {
            // Invalidate queries to reload data when tab changes
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            if (activeTab === 'formSubmissions') {
                queryClient.invalidateQueries({ queryKey: ['contactListSubmissions', selectedListView._id] });
            }
        }
    }, [activeTab, selectedListView?._id]);

    // Test QR code generation on component mount
    useEffect(() => {
        console.log('ContactListTab mounted, QRCode library available:', !!QRCode);
    }, []);

    // Force QR code regeneration when table is visible and data changes
    useEffect(() => {
        if (contactListsData?.data && !isContactListsLoading) {
            console.log('Table data loaded, forcing QR code generation...');
            // Wait for table to render completely
            setTimeout(() => {
                contactListsData.data.forEach((list: ContactList) => {
                    if (list.formQuestions && list.formQuestions.length > 0 && list.isFormActive) {
                        console.log(`Force generating QR for list: ${list.name} (${list._id})`);
                        generateQRCode(list._id);
                    }
                });
            }, 1000); // Longer delay to ensure table is fully rendered
        }
    }, [contactListsData, isContactListsLoading]);

    // Load form questions when editing a contact
    useEffect(() => {
        const loadEditContactFormQuestions = async () => {
            if (!editingContact || !editingContact.listId) {
                setEditContactFormQuestions([]);
                setEditContactFormAnswers({});
                setIsLoadingEditFormQuestions(false);
                return;
            }

            setIsLoadingEditFormQuestions(true);
            try {
                // Get form questions from the contact's list
                const list = await fetchContactListById(editingContact.listId);
                if (list && list.formQuestions && list.formQuestions.length > 0) {
                    setEditContactFormQuestions(list.formQuestions);
                    // Pre-populate answers from contact's formData
                    if (editingContact.formData) {
                        setEditContactFormAnswers(editingContact.formData);
                    } else {
                        setEditContactFormAnswers({});
                    }
                } else {
                    setEditContactFormQuestions([]);
                    setEditContactFormAnswers({});
                }
            } catch (error) {
                console.error('Error loading form questions for edit:', error);
                setEditContactFormQuestions([]);
                setEditContactFormAnswers({});
            } finally {
                setIsLoadingEditFormQuestions(false);
            }
        };

        if (editingContact) {
            loadEditContactFormQuestions();
        } else {
            setIsLoadingEditFormQuestions(false);
        }
    }, [editingContact]);

    // Edit contact handlers
    const handleEditContact = (contact: Contact) => {
        setEditingContact(contact);
        setEditContact({
            name: contact.name,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone,
            tags: contact.tags || [],
            listId: contact.listId
        });
        setEditEmailError('');
        onEditContactOpen();
    };

    const handleEditEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const email = e.target.value;
        setEditContact(prev => ({ ...prev, email }));

        // Clear error when user starts typing
        if (editEmailError) {
            setEditEmailError('');
        }
    };

    const handleEditEmailBlur = () => {
        if (editContact.email && !validateEmail(editContact.email)) {
            setEditEmailError('Please enter a valid email address');
        } else {
            setEditEmailError('');
        }
    };

    const handleEditFormQuestionChange = (questionId: string, value: string | string[]) => {
        setEditContactFormAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const renderEditFormQuestion = (question: FormQuestion) => {
        const answer = editContactFormAnswers[question.id];

        return (
            <div key={question.id} className="space-y-2">
                <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">
                        {question.title}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                </div>

                {question.description && (
                    <p className="text-xs text-grey50 dark:text-nightMuted">{question.description}</p>
                )}

                {question.type === 'short-answer' && (
                    <Input
                        classNames={inputCls}
                        placeholder="Your answer"
                        value={(answer as string) || ''}
                        onChange={(e) => handleEditFormQuestionChange(question.id, e.target.value)}
                        className="w-full"
                    />
                )}

                {question.type === 'long-answer' && (
                    <Textarea
                        classNames={textareaCls}
                        placeholder="Your answer"
                        value={(answer as string) || ''}
                        onChange={(e) => handleEditFormQuestionChange(question.id, e.target.value)}
                        className="w-full"
                        rows={4}
                    />
                )}

                {question.type === 'select' && (
                    <Select aria-label="Select" selectedKeys={[String(((answer as string) || '') ?? '')]} onChange={(e) => handleEditFormQuestionChange(question.id, e.target.value)}>
                        <SelectItem key="">Select an option</SelectItem>
                        {question.options?.map((option) => (                            <SelectItem key={String(option)}>{option}</SelectItem>
                        ))}
                    </Select>
                )}

                {question.type === 'checkbox' && (
                    <div className="space-y-2">
                        {question.options?.map((option, index) => {
                            const selectedOptions = (answer as string[]) || [];
                            const isChecked = selectedOptions.includes(option);

                            return (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id={`edit_${question.id}_${index}`}
                                        checked={isChecked}
                                        onChange={(e) => {
                                            const currentOptions = (answer as string[]) || [];
                                            if (e.target.checked) {
                                                handleEditFormQuestionChange(question.id, [...currentOptions, option]);
                                            } else {
                                                handleEditFormQuestionChange(question.id, currentOptions.filter(opt => opt !== option));
                                            }
                                        }}
                                        className={checkboxCls}
                                    />
                                    <label htmlFor={`edit_${question.id}_${index}`} className="text-sm text-grey20 dark:text-nightTextSecondary">
                                        {option}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const handleUpdateContact = async () => {
        if (!editingContact) return;

        // Validate email before submitting
        if (!editContact.email || !validateEmail(editContact.email)) {
            setEditEmailError('Please enter a valid email address');
            return;
        }

        const updateData: any = {
            ...editContact
        };

        // Include formData if there are form questions and answers
        if (editContactFormQuestions.length > 0 && Object.keys(editContactFormAnswers).length > 0) {
            updateData.formData = editContactFormAnswers;
        }

        updateContactMutation.mutate({
            id: editingContact._id,
            data: updateData
        });
    };

    const handleEditTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const input = e.currentTarget;
            const tag = input.value.trim();
            if (tag && !editContact.tags.includes(tag)) {
                setEditContact(prev => ({
                    ...prev,
                    tags: [...prev.tags, tag]
                }));
                input.value = '';
            }
        }
    };

    const removeEditTag = (tagToRemove: string) => {
        setEditContact(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    // CSV Import/Export functions
    const parseCSV = (csvText: string): any[] => {
        const lines = csvText.split('\n');
        const headers = lines[0].split(',').map(header => header.trim().toLowerCase().replace(/"/g, ''));
        const data = [];

        // Define the fields we want to extract
        const targetFields = ['name', 'lastName', 'email', 'phone', 'country', 'city', 'state'];
        const fieldMappings: { [key: string]: string[] } = {
            'name': ['name', 'first name', 'first_name', 'firstname', 'given name', 'given_name', 'givenname', 'fname', 'f_name'],
            'lastName': ['lastName', 'lastname', 'last name', 'last_name', 'surname', 'family name', 'family_name', 'familyname', 'lname', 'l_name'],
            'email': ['email', 'e-mail', 'e_mail', 'email address', 'email_address', 'mail', 'electronic mail'],
            'phone': ['phone', 'phone number', 'phone_number', 'phonenumber', 'mobile', 'mobile number', 'mobile_number', 'mobilenumber', 'telephone', 'tel', 'cell', 'cell phone', 'cell_phone', 'cellphone'],
            'country': ['country', 'nation', 'nationality', 'residence country', 'residence_country'],
            'city': ['city', 'town', 'municipality', 'locality', 'place'],
            'state': ['state', 'province', 'region', 'territory', 'county', 'district', 'area']
        };

        for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
                const values = parseCSVLine(lines[i]);
                const row: any = {};

                // Only extract the target fields
                targetFields.forEach(field => {
                    const possibleHeaders = fieldMappings[field];
                    let found = false;

                    for (const possibleHeader of possibleHeaders) {
                        const headerIndex = headers.findIndex(h => h.includes(possibleHeader));
                        if (headerIndex !== -1) {
                            row[field] = values[headerIndex] || '';
                            found = true;
                            break;
                        }
                    }

                    if (!found) {
                        row[field] = '';
                    }
                });

                data.push(row);
            }
        }
        return data;
    };

    const parseCSVLine = (line: string): string[] => {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    // Escaped quote
                    current += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        return result;
    };

    // Parse CSV for preview - shows all columns as they appear in the CSV
    const parseCSVForPreview = (csvText: string): any[] => {
        const lines = csvText.split('\n').filter(line => line.trim());
        if (lines.length === 0) return [];

        // Parse header row
        const headerLine = lines[0];
        const headers = parseCSVLine(headerLine).map(header => header.trim().replace(/^"|"$/g, ''));

        const data = [];
        // Parse data rows (skip header, show first 10 rows for preview)
        for (let i = 1; i < Math.min(lines.length, 11); i++) {
            if (lines[i].trim()) {
                const values = parseCSVLine(lines[i]);
                const row: any = {};

                // Map each header to its corresponding value
                headers.forEach((header, index) => {
                    row[header] = values[index] || '';
                });

                data.push(row);
            }
        }
        return data;
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
            setCsvFile(file);
            setCsvImportError('');

            const reader = new FileReader();
            reader.onload = (e) => {
                const csvText = e.target?.result as string;
                try {
                    // Use preview parser to show all columns
                    const previewData = parseCSVForPreview(csvText);
                    setCsvPreview(previewData);
                } catch (error) {
                    console.error('Error parsing CSV file:', error);
                    setCsvImportError('Error parsing CSV file. Please check the format.');
                    setCsvPreview([]);
                }
            };
            reader.readAsText(file);
        } else {
            setCsvImportError('Please select a valid CSV file.');
            setCsvPreview([]);
        }
    };

    // Parse full CSV with all columns for import
    const parseCSVForImport = (csvText: string): any[] => {
        const lines = csvText.split('\n').filter(line => line.trim());
        if (lines.length === 0) return [];

        // Parse header row
        const headerLine = lines[0];
        const headers = parseCSVLine(headerLine).map(header => header.trim().replace(/^"|"$/g, ''));

        const data = [];
        // Parse all data rows
        for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
                const values = parseCSVLine(lines[i]);
                const row: any = {};

                // Map each header to its corresponding value
                headers.forEach((header, index) => {
                    row[header] = values[index] || '';
                });

                data.push(row);
            }
        }
        return data;
    };

    // Helper function to map CSV row to contact with form questions
    const mapCSVRowToContact = (row: any, formQuestions: FormQuestion[], listId: string | null): any => {
        // Field mappings for main contact fields
        const fieldMappings: { [key: string]: string[] } = {
            'name': ['name', 'first name', 'first_name', 'firstname', 'given name', 'given_name', 'givenname', 'fname', 'f_name'],
            'lastName': ['lastName', 'lastname', 'last name', 'last_name', 'surname', 'family name', 'family_name', 'familyname', 'lname', 'l_name'],
            'email': ['email', 'e-mail', 'e_mail', 'email address', 'email_address', 'mail', 'electronic mail'],
            'phone': ['phone', 'phone number', 'phone_number', 'phonenumber', 'mobile', 'mobile number', 'mobile_number', 'mobilenumber', 'telephone', 'tel', 'cell', 'cell phone', 'cell_phone', 'cellphone'],
            'country': ['country', 'nation', 'nationality', 'residence country', 'residence_country'],
            'city': ['city', 'town', 'municipality', 'locality', 'place'],
            'state': ['state', 'province', 'region', 'territory', 'county', 'district', 'area']
        };

        const contact: any = {
            name: '',
            lastName: '',
            email: '',
            phone: '',
            country: '',
            city: '',
            state: '',
            tags: [],
            listId: listId || undefined,
            formData: {}
        };

        // Map main fields from CSV
        Object.keys(fieldMappings).forEach(field => {
            const possibleHeaders = fieldMappings[field];
            for (const possibleHeader of possibleHeaders) {
                // Check exact match first, then case-insensitive contains
                const headerKey = Object.keys(row).find(h =>
                    h.toLowerCase() === possibleHeader.toLowerCase() ||
                    h.toLowerCase().includes(possibleHeader.toLowerCase())
                );
                if (headerKey && row[headerKey]) {
                    contact[field] = String(row[headerKey]).trim();
                    break;
                }
            }
        });

        // Map form question answers from CSV
        formQuestions.forEach((question: FormQuestion) => {
            const questionTitle = question.title;
            // Try to find column that matches question title (case-insensitive)
            const csvColumnKey = Object.keys(row).find(key =>
                key.toLowerCase() === questionTitle.toLowerCase() ||
                key.toLowerCase().replace(/[_\s]/g, '').includes(questionTitle.toLowerCase().replace(/[_\s]/g, ''))
            );

            if (csvColumnKey && row[csvColumnKey] !== undefined && row[csvColumnKey] !== null && String(row[csvColumnKey]).trim() !== '') {
                const csvValue = String(row[csvColumnKey]).trim();

                // Handle checkbox values (comma-separated) or single values
                if (question.type === 'checkbox') {
                    // Split comma-separated values for checkboxes
                    const values = csvValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                    if (values.length > 0) {
                        contact.formData[question.id] = values;
                    }
                } else {
                    contact.formData[question.id] = csvValue;
                }
            }
        });

        // Only include formData if it has values
        if (Object.keys(contact.formData).length === 0) {
            delete contact.formData;
        }

        return contact;
    };


    const handleCsvImport = async () => {
        if (!csvFile) return;

        setIsCsvImporting(true);
        setCsvImportError('');

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const csvText = e.target?.result as string;
                try {
                    // Parse full CSV with all columns
                    const fullCSVData = parseCSVForImport(csvText);

                    if (fullCSVData.length === 0) {
                        setCsvImportError('No data found in CSV file.');
                        setIsCsvImporting(false);
                        return;
                    }

                    // Get form questions from the selected list for import
                    const listId = csvImportListId || (selectedListView ? selectedListView._id : null);
                    let listForImport = listId
                        ? contactLists.find((list: ContactList) => list._id === listId)
                        : selectedListView;

                    // If list doesn't have form questions, fetch them
                    let formQuestions: FormQuestion[] = [];
                    if (listForImport?.formQuestions) {
                        formQuestions = listForImport.formQuestions;
                    } else if (listId) {
                        // Fetch list details to get form questions
                        try {
                            const listDetails = await fetchContactListById(listId);
                            if (listDetails?.formQuestions) {
                                formQuestions = listDetails.formQuestions;
                            }
                        } catch (error) {
                            console.warn('Could not fetch form questions for list:', listId, error);
                        }
                    }

                    // Map CSV rows to contacts (this will extract main fields and form questions)
                    const mappedContacts = fullCSVData.map(row => {
                        return mapCSVRowToContact(row, formQuestions, listId);
                    });

                    // Filter out invalid contacts (must have name and email)
                    const validContacts = mappedContacts.filter(contact => contact.name && contact.email);

                    if (validContacts.length === 0) {
                        setCsvImportError('No valid contacts found. Please check that your CSV has name and email columns.');
                        setIsCsvImporting(false);
                        return;
                    }

                    // Create mapped JSON content
                    const mappedJSON = JSON.stringify(validContacts, null, 2);

                    // Create a temporary JSON file
                    const tempFile = new File([mappedJSON], 'mapped_contacts.json', { type: 'application/json' });

                    // Create FormData for upload
                    const formData = new FormData();
                    formData.append('jsonFile', tempFile);
                    if (listId) {
                        formData.append('listId', listId);
                    }

                    // Add metadata
                    formData.append('totalContacts', validContacts.length.toString());
                    formData.append('importType', 'csv');

                    // Debug: Log FormData contents
                    console.log('FormData contents:');
                    for (let [key, value] of formData.entries()) {
                        console.log(`${key}:`, value);
                    }

                    // Upload to backend for bulk processing
                    const result = await bulkImportContactsFromCSV(formData);

                    if (result.success) {
                        queryClient.invalidateQueries({ queryKey: ['contacts'] });
                        onImportCsvClose();
                        setCsvFile(null);
                        setCsvPreview([]);
                        setCsvImportError('');
                        setCsvImportListId('');

                        const importedCount = result.importedCount || 0;
                        const updatedCount = result.updatedCount || 0;
                        const totalProcessed = result.totalProcessed || 0;

                        let message = `Successfully processed ${totalProcessed} contacts!`;
                        if (importedCount > 0 && updatedCount > 0) {
                            message += ` ${importedCount} new, ${updatedCount} updated.`;
                        } else if (importedCount > 0) {
                            message += ` ${importedCount} new contacts.`;
                        } else if (updatedCount > 0) {
                            message += ` ${updatedCount} contacts updated.`;
                        }
                        alert(message);
                    } else {
                        setCsvImportError(result.message || 'Error importing contacts. Please check the CSV format.');
                    }
                } catch (error) {
                    setCsvImportError('Error processing CSV file. Please check the format.');
                } finally {
                    setIsCsvImporting(false);
                }
            };
            reader.readAsText(csvFile);
        } catch (error: any) {
            console.error('Error importing CSV:', error);
            setCsvImportError(error.response?.data?.message || 'Error importing contacts. Please try again.');
            setIsCsvImporting(false);
        }
    };

    const handleCsvExport = async () => {
        const listIdToUse = selectedListForExport || selectedListView?._id;
        if (!listIdToUse) {
            alert('Please select a list to export');
            return;
        }

        setIsExportingCsv(true);
        try {
            // Fetch all contacts for the selected list (with a high limit to get all)
            const exportContactsData = await fetchContacts({
                page: 1,
                limit: 10000, // High limit to get all contacts
                list: listIdToUse
            });

            const listContacts = exportContactsData?.data || [];

            if (listContacts.length === 0) {
                alert('No contacts found in the selected list');
                setIsExportingCsv(false);
                return;
            }

            // Get the selected list to access form questions
            const selectedListForExportData = contactLists.find((list: ContactList) => list._id === listIdToUse) || selectedListView;
            const formQuestions = selectedListForExportData?.formQuestions || [];

            // Build CSV data with base fields, creation date, and form questions/answers
            const baseHeaders = ['name', 'lastName', 'email', 'phone', 'createdAt'];
            const questionHeaders = formQuestions.map((q: FormQuestion) => q.title);
            const headers = [...baseHeaders, ...questionHeaders];

            const csvData = listContacts.map((contact: Contact) => {
                const baseData: { [key: string]: string } = {
                    name: contact.name || '',
                    lastName: contact.lastName || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    createdAt: contact.createdAt ? new Date(contact.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : ''
                };

                // Add form question answers
                formQuestions.forEach((question: FormQuestion) => {
                    const answer = contact.formData?.[question.id];
                    if (answer !== undefined && answer !== null) {
                        if (Array.isArray(answer)) {
                            baseData[question.title] = answer.join(', ');
                        } else {
                            baseData[question.title] = String(answer);
                        }
                    } else {
                        baseData[question.title] = '';
                    }
                });

                return baseData;
            });

            const csvContent = [
                headers.join(','),
                ...csvData.map((row: { [key: string]: string }) => headers.map(header => {
                    const value = row[header] || '';
                    // Escape quotes in the value and wrap in quotes
                    return `"${String(value).replace(/"/g, '""')}"`;
                }).join(','))
            ].join('\n');

            const listIdForName = selectedListForExport || selectedListView?._id;
            const selectedListName = contactLists.find((list: ContactList) => list._id === listIdForName)?.name || selectedListView?.name || 'contacts';
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${selectedListName}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            onExportCsvClose();
            setSelectedListForExport('');
            setIsExportingCsv(false);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Failed to export contacts. Please try again.');
            setIsExportingCsv(false);
        }
    };

    // Show loading while initializing from URL to prevent flash of list view
    if (isInitializingFromUrl && isContactListsLoading) {
        return (
            <div>
                <div className="mb-6 flex items-center justify-between">
                    <div className={`h-10 w-56 ${skel}`}></div>
                    <div className={`h-10 w-28 ${skel}`}></div>
                </div>
                <div className={`${card} overflow-hidden mb-[100px]`}>
                    <table className="w-full">
                        <thead>
                            <tr className="bg-grey99/70 dark:bg-white/[0.03]">
                                <th className="px-4 py-3 text-left">
                                    <div className="h-4 w-4 animate-pulse rounded bg-grey95 dark:bg-white/10"></div>
                                </th>
                                <th className={thCls}>Name</th>
                                <th className={thCls}>Contacts</th>
                                <th className={thCls}>Created</th>
                                <th className={thCls}>Last Updated</th>
                                <th className={thCls}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...Array(5)].map((_, index) => (
                                <tr key={`skeleton-init-${index}`} className={index % 2 === 0 ? rowCls : `${rowCls} bg-grey99/40 dark:bg-white/[0.02]`}>
                                    <td className="px-4 py-3">
                                        <div className="h-4 w-4 animate-pulse rounded bg-grey95 dark:bg-white/10"></div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`h-4 w-32 ${skel}`}></div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`h-4 w-16 ${skel}`}></div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`h-4 w-24 ${skel}`}></div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`h-4 w-24 ${skel}`}></div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`h-4 w-12 ${skel}`}></div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className="">
            <style>{`
                .ProseMirror {
                    outline: none;
                    min-height: 200px;
                    max-height: 300px;
                    overflow-y: auto;
                }
                .ProseMirror h1 {
                    font-size: 1.5rem;
                    font-weight: bold;
                    margin: 1rem 0 0.5rem 0;
                }
                .ProseMirror h2 {
                    font-size: 1.25rem;
                    font-weight: bold;
                    margin: 0.75rem 0 0.5rem 0;
                }
                .ProseMirror h3 {
                    font-size: 1.125rem;
                    font-weight: bold;
                    margin: 0.5rem 0 0.25rem 0;
                }
                .ProseMirror ul {
                    list-style-type: disc;
                    margin: 0.5rem 0;
                    padding-left: 1.5rem;
                }
                .ProseMirror ol {
                    list-style-type: decimal;
                    margin: 0.5rem 0;
                    padding-left: 1.5rem;
                }
                .ProseMirror li {
                    margin: 0.25rem 0;
                }
                .ProseMirror blockquote {
                    border-left: 4px solid #e5e7eb;
                    padding-left: 1rem;
                    margin: 1rem 0;
                    font-style: italic;
                    color: #6b7280;
                }
                .ProseMirror img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 0.5rem;
                    margin: 0.5rem 0;
                }
            `}</style>
            {isContactsError && (
                <div className="mb-4 rounded-2xl bg-red-50 p-4 ring-1 ring-red-200/60 dark:bg-red-500/10 dark:ring-red-500/20">
                    <p className="text-red-800 dark:text-red-400">Error loading contacts: {contactsError?.message || 'An error occurred'}</p>
                </div>
            )}

            {/* List View - Show when no list is selected and not initializing from URL */}
            {!selectedListView && !isInitializingFromUrl ? (
                <>


                    {/* Contact Lists Table */}
                    <div>
                        {/* Header with count and New List button */}
                        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                            {isContactListsLoading ? (
                                <div className={`h-10 w-56 ${skel}`}></div>
                            ) : (
                                <h1 className="text-[clamp(24px,3vw,32px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">
                                    {contactLists.length} Lists - {contactLists.reduce((sum: number, list: ContactList) => sum + (list.contactCount || 0), 0)} Contacts
                                </h1>
                            )}
                            <Button
                                onClick={onNewListOpen}
                                color="primary"
                                size="sm"
                                className={accentBtnSm}
                            >
                                New List
                            </Button>
                        </div>
                        <div className={`${card} mb-[100px] hidden overflow-hidden md:block`}>
                            <div className="max-h-[calc(100vh-300px)] min-h-[280px] overflow-auto overscroll-contain">
                                <table className="w-full min-w-[640px] border-separate border-spacing-0">
                                    <thead>
                                        <tr>
                                            <th className={`${thStick} left-0 z-30 w-12`}>
                                                <input type="checkbox" className={checkboxCls} aria-label="Select all lists" />
                                            </th>
                                            <th className={`${thStick} left-12 z-30 min-w-[200px]`}>Name</th>
                                            <th className={`${thStick} z-20`}>Contacts</th>
                                            <th className={`${thStick} z-20`}>Created</th>
                                            <th className={`${thStick} z-20`}>Last Updated</th>
                                            <th className={`${thStick} right-0 z-30 w-[148px]`}><span className="sr-only">Actions</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isContactListsLoading ? (
                                            [...Array(5)].map((_, index) => (
                                                <tr key={`skeleton-${index}`}>
                                                    <td className={tdCls}><div className="h-4 w-4 animate-pulse rounded bg-grey95 dark:bg-white/10"></div></td>
                                                    <td className={tdCls}><div className={`h-4 w-32 ${skel}`}></div></td>
                                                    <td className={tdCls}><div className={`h-4 w-16 ${skel}`}></div></td>
                                                    <td className={tdCls}><div className={`h-4 w-24 ${skel}`}></div></td>
                                                    <td className={tdCls}><div className={`h-4 w-24 ${skel}`}></div></td>
                                                    <td className={tdCls}><div className={`h-4 w-12 ${skel}`}></div></td>
                                                </tr>
                                            ))
                                        ) : contactLists.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-16 text-center text-sm text-grey40 dark:text-nightMuted">
                                                    No contact lists found. Create your first list to get started.
                                                </td>
                                            </tr>
                                        ) : (
                                            contactLists.map((list: ContactList) => (
                                                <tr
                                                    key={list._id}
                                                    onClick={() => {
                                                        setSelectedListView(list);
                                                        setActiveTab('contacts');
                                                    }}
                                                    className="group cursor-pointer"
                                                >
                                                    <td className={`${tdStick} left-0 z-10 w-12`} onClick={(e) => e.stopPropagation()}>
                                                        <input type="checkbox" className={checkboxCls} aria-label={`Select ${list.name}`} />
                                                    </td>
                                                    <td className={`${tdStick} left-12 z-10`}>
                                                        <div className={`${cellText} max-w-[320px] truncate`} title={list.name}>{list.name}</div>
                                                    </td>
                                                    <td className={tdCls}>
                                                        <div className={cellText}>{list.contactCount || 0}</div>
                                                    </td>
                                                    <td className={tdCls}>
                                                        <div className={`${cellText} whitespace-nowrap`}>
                                                            {new Date(list.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}
                                                        </div>
                                                    </td>
                                                    <td className={tdCls}>
                                                        <div className={`${cellText} whitespace-nowrap`}>
                                                            {new Date(list.updatedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}
                                                        </div>
                                                    </td>
                                                    <td className={`${tdStick} right-0 z-10 w-[148px]`}>
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedListView(list);
                                                                    setActiveTab('contacts');
                                                                }}
                                                                className={iconBtn}
                                                                aria-label="View list"
                                                                title="View list"
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                                    <circle cx="12" cy="12" r="3"></circle>
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingList(list);
                                                                    setEditList({
                                                                        name: list.name,
                                                                        description: list.description || '',
                                                                        organizationId: list.organizationId || selectedOrganizerId || undefined
                                                                    });
                                                                    onEditListOpen();
                                                                }}
                                                                className={iconBtn}
                                                                aria-label="Edit list"
                                                                title="Edit"
                                                            >
                                                                <Edit width={16} height={16} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteList(list._id, list.name);
                                                                }}
                                                                className={iconBtnDanger}
                                                                aria-label="Delete list"
                                                                title="Delete"
                                                            >
                                                                <Trash width={16} height={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="md:hidden pb-[100px] flex flex-col gap-3">
                            {isContactListsLoading ? (
                                [...Array(5)].map((_, index) => (
                                    <div key={`skeleton-m-${index}`} className={`${card} animate-pulse p-5`}>
                                        <div className="mb-2 h-4 w-32 rounded-full bg-grey95 dark:bg-white/10"></div>
                                        <div className="h-3 w-20 rounded-full bg-grey95 dark:bg-white/10"></div>
                                    </div>
                                ))
                            ) : contactLists.length === 0 ? (
                                <div className="rounded-[28px] bg-grey99 px-6 py-12 text-center text-sm text-grey40 dark:bg-white/[0.04] dark:text-nightMuted">
                                    No contact lists found. Create your first list to get started.
                                </div>
                            ) : (
                                contactLists.map((list: ContactList) => (
                                    <div
                                        key={list._id}
                                        onClick={() => {
                                            setSelectedListView(list);
                                            setActiveTab('contacts');
                                        }}
                                        className={`${card} cursor-pointer p-5 transition-colors active:bg-grey99 dark:active:bg-white/[0.08]`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="truncate text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">{list.name}</div>
                                                <div className="mt-0.5 text-xs text-grey50 dark:text-nightMuted">{list.contactCount || 0} contacts</div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingList(list);
                                                        setEditList({
                                                            name: list.name,
                                                            description: list.description || '',
                                                            organizationId: list.organizationId || selectedOrganizerId || undefined
                                                        });
                                                        onEditListOpen();
                                                    }}
                                                    className={iconBtn}
                                                    aria-label="Edit list"
                                                >
                                                    <Edit width={16} height={16} />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteList(list._id, list.name);
                                                    }}
                                                    className={iconBtnDanger}
                                                    aria-label="Delete list"
                                                >
                                                    <Trash width={16} height={16} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-4 flex items-center gap-4 text-xs font-medium text-grey50 dark:text-nightMuted">
                                            <span>Created {new Date(list.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                                            <span>Updated {new Date(list.updatedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            ) : selectedListView ? (
                <>
                    {/* Detail View - Show when a list is selected */}
                    {/* Breadcrumb */}
                    <div className="mb-6">
                        <button
                            onClick={() => {
                                setSelectedListView(null);
                                setActiveTab('contacts');
                                // Refetch contact lists when going back
                                refetchContactLists();
                                // URL will be updated by useEffect
                            }}
                            className="group flex items-center gap-3 text-left"
                        >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-grey99 text-grey20 ring-1 ring-black/[0.04] transition-colors group-hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:group-hover:bg-white/10">←</span>
                            <span className="text-[clamp(24px,3vw,32px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">{selectedListView.name}</span>
                        </button>
                    </div>

                    {/* Active Email Jobs Section - Outside tabs */}
                    {selectedListView && activeEmailJobs && activeEmailJobs.length > 0 && (
                        <div className={`${surface} mb-6 space-y-4 p-5 sm:p-6`}>
                            <h3 className={sectionHeading}>
                                Active Email Jobs
                            </h3>
                            <div className="space-y-3">
                                {activeEmailJobs.map((job: any) => {
                                    const progressPercentage = job.progress || 0;
                                    return (
                                        <div
                                            key={job._id}
                                            className={`${card} p-5`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h4 className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">{job.subject}</h4>
                                                        <span
                                                            className={`${pill} capitalize ${job.status === 'completed'
                                                                ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                                                : job.status === 'processing'
                                                                    ? 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent'
                                                                    : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400'
                                                                }`}
                                                        >
                                                            {job.status}
                                                        </span>
                                                    </div>
                                                    <div className="mb-3">
                                                        <div className="mb-2 flex items-center justify-between text-sm font-medium text-grey40 dark:text-nightMuted">
                                                            <span>Progress: {progressPercentage}%</span>
                                                            <span>{job.sentCount || 0} / {job.totalRecipients || 0} sent</span>
                                                        </div>
                                                        <div className="h-2 w-full rounded-full bg-grey95 dark:bg-white/10">
                                                            <div
                                                                className="h-2 rounded-full bg-primary transition-all duration-300 dark:bg-nightAccent"
                                                                style={{ width: `${progressPercentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    <div className="mb-3 grid grid-cols-1 gap-2 text-sm text-grey40 dark:text-nightMuted sm:grid-cols-3 sm:gap-4">
                                                        <div>
                                                            <span className="font-medium">Total:</span> {job.totalRecipients || 0}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Sent:</span> {job.sentCount || 0}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Failed:</span> {job.failedCount || 0}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-grey50 dark:text-nightMuted">
                                                        Started: {job.startedAt ? new Date(job.startedAt).toLocaleString() : new Date(job.createdAt).toLocaleString()}
                                                        {job.remaining > 0 && (
                                                            <> | Remaining: {job.remaining}</>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}



                    {/* Tabs */}
                    <div className="mb-6">
                        <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
                            <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                                <Segmented
                                    label="Contact list sections"
                                    value={activeTab}
                                    onChange={(key) => setActiveTab(key)}
                                    options={[
                                        { key: 'contacts', label: 'Contacts' },
                                        { key: 'emailHistory', label: 'Email History' },
                                        { key: 'formBuilder', label: 'Entry Form' },
                                    ]}
                                />
                            </div>
                            {activeTab === 'contacts' && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        onClick={() => {
                                            if (selectedListView) {
                                                setCsvImportListId(selectedListView._id);
                                            }
                                            onImportCsvOpen();
                                        }}
                                        variant="light"
                                        size="sm"
                                        className={toolBtn}
                                        startContent={
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="7 10 12 15 17 10"></polyline>
                                                <line x1="12" y1="15" x2="12" y2="3"></line>
                                            </svg>
                                        }
                                    >
                                        Import
                                    </Button>
                                    <Button
                                        onClick={() => {


                                            if (selectedListView) {
                                                setEmailData(prev => ({ ...prev, listId: selectedListView._id }));
                                            }
                                            if (activeEmailJobs?.length <= 0) {
                                                onSendEmailOpen();
                                            }
                                        }}
                                        variant="light"
                                        size="sm"
                                        isDisabled={activeEmailJobs?.length > 0}
                                        className={toolBtn}
                                        startContent={
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                <polyline points="22,6 12,13 2,6"></polyline>
                                            </svg>
                                        }
                                    >
                                        Send Email
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (selectedListView) {
                                                setSelectedListForExport(selectedListView._id);
                                            } else {
                                                setSelectedListForExport('');
                                            }
                                            onExportCsvOpen();
                                        }}
                                        variant="light"
                                        size="sm"
                                        className={toolBtn}
                                        startContent={
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="17 8 12 3 7 8"></polyline>
                                                <line x1="12" y1="3" x2="12" y2="15"></line>
                                            </svg>
                                        }
                                    >
                                        Export
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (selectedListView) {
                                                setSelectedListForNewContact(selectedListView._id);
                                            } else {
                                                setSelectedListForNewContact('');
                                            }
                                            onAddContactOpen();
                                        }}
                                        color="primary"
                                        size="sm"
                                        className={accentBtnSm}
                                    >
                                        Add Contact
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {activeTab === 'contacts' && (
                        <div>
                            {/* Table Header with Filters */}
                            <div className="mb-4 flex items-center justify-between flex-wrap gap-4 ">
                                <div className="flex items-center gap-4 w-full sm:w-auto">
                                    <div className="relative w-full sm:w-auto">
                                        <input
                                            type="text"
                                            placeholder="Search contacts"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className={`${inputBase} pl-11 sm:w-80`}
                                        />
                                        <svg
                                            className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-grey60 dark:text-nightMuted"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    {/* <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary40 focus:border-transparent"
                >
                                        <option value="all">Entry Method: All</option>
                                        {allTags.map((tag: string) => (
                        <option key={tag} value={tag}>{tag}</option>
                    ))}
                </select>
                                    <Select aria-label="Select">
                                        <SelectItem key="all">Event: All</SelectItem>
                                    </Select> */}
                                </div>
                                {selectedContacts.length > 0 && (
                                    <Button
                                        onClick={handleBulkDelete}
                                        color="danger"
                                        size="sm"
                                        className="h-10 w-full rounded-full px-4 text-sm font-semibold sm:w-auto"
                                        startContent={
                                            <Trash width={16} height={16} color="white" />
                                        }
                                    >
                                        Delete Selected Items
                                    </Button>
                                )}
                            </div>

                            {/* Contacts Table */}
                            <div>
                                <div className={`${card} hidden overflow-hidden md:block`}>
                                    <div className="max-h-[calc(100vh-360px)] min-h-[320px] overflow-auto overscroll-contain">
                                        <table className="w-full min-w-[800px] border-separate border-spacing-0">
                                            <thead>
                                                <tr>
                                                    <th className={`${thStick} left-0 z-30 w-12`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedContacts.length === contacts.length && contacts.length > 0}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedContacts(contacts.map((c: Contact) => c._id));
                                                                } else {
                                                                    setSelectedContacts([]);
                                                                }
                                                            }}
                                                            className={checkboxCls}
                                                            aria-label="Select all contacts"
                                                        />
                                                    </th>
                                                    <th className={`${thStick} left-12 z-30 min-w-[180px]`}>Name</th>
                                                    <th className={`${thStick} z-20 min-w-[200px]`}>Email</th>
                                                    <th className={`${thStick} z-20 min-w-[140px]`}>Phone</th>
                                                    <th className={`${thStick} z-20 min-w-[180px]`}>Created</th>
                                                    {selectedListView?.formQuestions?.map((question: FormQuestion) => (
                                                        <th key={question.id} className={`${thStick} z-20 min-w-[160px]`}>
                                                            <span className="flex items-center gap-1">
                                                                <span className="truncate">{question.title}</span>
                                                                {question.required && <span className="text-red-500">*</span>}
                                                            </span>
                                                        </th>
                                                    ))}
                                                    <th className={`${thStick} right-0 z-30 w-[104px]`}><span className="sr-only">Actions</span></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isContactsLoading ? (
                                                    [...Array(5)].map((_, index) => (
                                                        <tr key={`skeleton-${index}`}>
                                                            <td className={tdCls}><div className="h-4 w-4 animate-pulse rounded bg-grey95 dark:bg-white/10"></div></td>
                                                            <td className={tdCls}><div className={`h-4 w-32 ${skel}`}></div></td>
                                                            <td className={tdCls}><div className={`h-4 w-40 ${skel}`}></div></td>
                                                            <td className={tdCls}><div className={`h-4 w-28 ${skel}`}></div></td>
                                                            <td className={tdCls}><div className={`h-4 w-32 ${skel}`}></div></td>
                                                            {selectedListView?.formQuestions?.map((question: FormQuestion) => (
                                                                <td key={question.id} className={tdCls}><div className={`h-4 w-24 ${skel}`}></div></td>
                                                            ))}
                                                            <td className={tdCls}><div className={`h-4 w-12 ${skel}`}></div></td>
                                                        </tr>
                                                    ))
                                                ) : contacts.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5 + (selectedListView?.formQuestions?.length || 0) + 1} className="px-4 py-16 text-center text-sm text-grey40 dark:text-nightMuted">
                                                            No contacts found in this list. Add contacts to get started.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    contacts.map((contact: Contact) => (
                                                        <tr key={contact._id} className="group">
                                                            <td className={`${tdStick} left-0 z-10 w-12`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedContacts.includes(contact._id)}
                                                                    onChange={() => handleSelectContact(contact._id)}
                                                                    className={checkboxCls}
                                                                    aria-label={`Select ${contact.name} ${contact.lastName}`}
                                                                />
                                                            </td>
                                                            <td className={`${tdStick} left-12 z-10`}>
                                                                <div className={`${cellText} max-w-[240px] truncate`} title={`${contact.name} ${contact.lastName}`}>
                                                                    {contact.name} {contact.lastName}
                                                                </div>
                                                            </td>
                                                            <td className={tdCls}>
                                                                <div className={`${cellText} max-w-[260px] truncate`} title={contact.email}>{contact.email}</div>
                                                            </td>
                                                            <td className={tdCls}>
                                                                <div className={`${cellText} whitespace-nowrap`}>{contact.phone || '-'}</div>
                                                            </td>
                                                            <td className={tdCls}>
                                                                <div className={`${cellText} whitespace-nowrap`}>
                                                                    {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString('en-US', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    }) : '-'}
                                                                </div>
                                                            </td>
                                                            {selectedListView?.formQuestions?.map((question: FormQuestion) => {
                                                                const answer = contact.formData?.[question.id];
                                                                const answerValue = Array.isArray(answer) ? answer.join(', ') : (answer ? String(answer) : null);
                                                                return (
                                                                    <td key={question.id} className={tdCls}>
                                                                        <div className={`${cellText} max-w-[240px] truncate`} title={answerValue || undefined}>
                                                                            {answerValue ? (
                                                                                <span className="text-grey40 dark:text-nightMuted">{answerValue}</span>
                                                                            ) : (
                                                                                <span className="text-xs text-grey60 dark:text-nightMuted">-</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                );
                                                            })}
                                                            <td className={`${tdStick} right-0 z-10 w-[104px]`}>
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button onClick={() => handleEditContact(contact)} className={iconBtn} aria-label="Edit contact" title="Edit">
                                                                        <Edit width={16} height={16} />
                                                                    </button>
                                                                    <button onClick={() => handleDeleteContact(contact._id)} className={iconBtnDanger} aria-label="Delete contact" title="Delete">
                                                                        <Trash width={16} height={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="md:hidden flex flex-col gap-3">
                                    {isContactsLoading ? (
                                        [...Array(5)].map((_, index) => (
                                            <div key={`skeleton-cm-${index}`} className={`${card} animate-pulse p-5`}>
                                                <div className="mb-2 h-4 w-32 rounded-full bg-grey95 dark:bg-white/10"></div>
                                                <div className="mb-1 h-3 w-40 rounded-full bg-grey95 dark:bg-white/10"></div>
                                                <div className="h-3 w-24 rounded-full bg-grey95 dark:bg-white/10"></div>
                                            </div>
                                        ))
                                    ) : contacts.length === 0 ? (
                                        <div className="rounded-[28px] bg-grey99 px-6 py-12 text-center text-sm text-grey40 dark:bg-white/[0.04] dark:text-nightMuted">
                                            No contacts found in this list. Add contacts to get started.
                                        </div>
                                    ) : (
                                        contacts.map((contact: Contact) => (
                                            <div
                                                key={contact._id}
                                                className={`${card} p-5`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-start gap-3 min-w-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedContacts.includes(contact._id)}
                                                            onChange={() => handleSelectContact(contact._id)}
                                                            className={`mt-1 shrink-0 ${checkboxCls}`}
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="truncate text-sm font-semibold text-grey10 dark:text-white">
                                                                {contact.name} {contact.lastName}
                                                            </div>
                                                            <div className="mt-0.5 truncate text-xs text-grey50 dark:text-nightMuted">{contact.email}</div>
                                                            {contact.phone && (
                                                                <div className="mt-0.5 text-xs text-grey50 dark:text-nightMuted">{contact.phone}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <button
                                                            onClick={() => handleEditContact(contact)}
                                                            className={iconBtn}
                                                            aria-label="Edit contact"
                                                        >
                                                            <Edit width={16} height={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteContact(contact._id)}
                                                            className={iconBtnDanger}
                                                            aria-label="Delete contact"
                                                        >
                                                            <Trash width={16} height={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                                {(selectedListView?.formQuestions && selectedListView.formQuestions.length > 0) || contact.createdAt ? (
                                                    <div className="mt-4 flex flex-col gap-1.5 border-t border-black/[0.06] pt-4 dark:border-white/10">
                                                        {contact.createdAt && (
                                                            <div className="flex justify-between gap-2 text-xs">
                                                                <span className="text-grey50 dark:text-nightMuted">Created</span>
                                                                <span className="text-right font-medium text-grey20 dark:text-nightTextSecondary">
                                                                    {new Date(contact.createdAt).toLocaleDateString('en-US', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedListView?.formQuestions?.map((question: FormQuestion) => {
                                                            const answer = contact.formData?.[question.id];
                                                            const answerValue = Array.isArray(answer) ? answer.join(', ') : (answer ? String(answer) : null);
                                                            return (
                                                                <div key={question.id} className="flex justify-between gap-2 text-xs">
                                                                    <span className="shrink-0 text-grey50 dark:text-nightMuted">{question.title}</span>
                                                                    <span className="break-words text-right font-medium text-grey20 dark:text-nightTextSecondary">{answerValue || '-'}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Pagination */}
                            {/* Pagination Controls - Connected to Backend */}
                            <div className="mt-4 flex flex-col items-center gap-3 py-2 sm:flex-row sm:justify-between">
                                <div className="flex flex-wrap items-center justify-center gap-2 text-xs font-medium text-grey40 dark:text-nightMuted">
                                    <Select classNames={{ trigger: 'h-10 px-3 text-sm' }} aria-label="Select" selectedKeys={[String(rowsPerPage ?? '')]} onChange={e => setRowsPerPage(Number(e.target.value))}>
                                        <SelectItem key={String(10)}>10 Rows</SelectItem>
                                        <SelectItem key={String(25)}>25 Rows</SelectItem>
                                        <SelectItem key={String(50)}>50 Rows</SelectItem>
                                    </Select>
                                    <span className="ml-2">
                                        {contactLists?.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} - {currentPage * rowsPerPage} of {totalContacts}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap justify-center">
                                    <button
                                        className={`${iconBtn} disabled:opacity-50`}
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                    >
                                        <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                            <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>

                                    {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                                        const pageNum = i + Math.max(1, currentPage - 1);
                                        return pageNum <= totalPages ? (
                                            <button
                                                key={pageNum}
                                                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold transition-colors ${currentPage === pageNum
                                                    ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                    : 'bg-grey99 text-grey20 hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10'
                                                    }`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        ) : null;
                                    })}

                                    <button
                                        className={iconBtn}
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                    >
                                        <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex flex-wrap items-center justify-center gap-2 text-xs font-medium text-grey40 dark:text-nightMuted">
                                    <span>Go to page</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={totalPages}
                                        value={currentPage}
                                        onChange={e => setCurrentPage(Number(e.target.value))}
                                        className="h-9 w-14 rounded-full bg-grey99 px-2 text-center text-xs font-semibold text-grey10 ring-1 ring-black/[0.04] focus:outline-none dark:bg-white/[0.06] dark:text-white dark:ring-white/10"
                                    />
                                    <button
                                        className={`h-9 rounded-full px-4 ${currentPage === totalPages ? 'cursor-not-allowed bg-grey90 dark:bg-white/10' : 'bg-primary dark:bg-nightAccent dark:text-nightBg'} text-white text-xs font-semibold transition-colors`}
                                        onClick={() => setCurrentPage(currentPage)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Go
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'emailHistory' && (
                        <div className="space-y-6">
                            {/* Email History Section */}
                            <div>
                                <h3 className={`${sectionHeading} mb-4`}>
                                    Email History
                                    {selectedListView && ` - ${selectedListView.name}`}
                                </h3>
                                {!selectedListView ? (
                                    <div className="rounded-[28px] bg-grey99 px-6 py-12 text-center text-grey40 dark:bg-white/[0.04] dark:text-nightMuted">
                                        Please select a contact list to view email history.
                                    </div>
                                ) : isEmailRequestsLoading ? (
                                    <div className="text-center py-12">
                                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary dark:border-nightAccent"></div>
                                        <p className="mt-4 text-sm text-grey40 dark:text-nightMuted">Loading email history...</p>
                                    </div>
                                ) : emailRequests.length === 0 ? (
                                    <div className="rounded-[28px] bg-grey99 px-6 py-12 text-center text-grey40 dark:bg-white/[0.04] dark:text-nightMuted">
                                        No email requests found for this list.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {emailRequests.map((request: any) => {
                                            return (
                                                <div
                                                    key={request.id}
                                                    className={`${card} p-5 transition-colors hover:bg-grey99/60 dark:hover:bg-white/[0.06]`}
                                                >
                                                    <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <h4 className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">{request.subject}</h4>
                                                                <span
                                                                    className={`${pill} capitalize ${request.status === 'completed'
                                                                        ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                                                        : request.status === 'processing'
                                                                            ? 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent'
                                                                            : request.status === 'failed'
                                                                                ? 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400'
                                                                                : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400'
                                                                        }`}
                                                                >
                                                                    {request.status}
                                                                </span>
                                                            </div>
                                                            <div className="mb-3 grid grid-cols-1 gap-2 text-sm text-grey40 dark:text-nightMuted sm:grid-cols-3 sm:gap-4">
                                                                <div>
                                                                    <span className="font-medium">Total:</span> {request.totalRecipients}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Sent:</span> <span className="text-green-600 dark:text-green-400">{request.sentCount}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Failed:</span> {request.failedCount > 0 ? (
                                                                        <span className="text-red-600 dark:text-red-400 font-semibold">{request.failedCount}</span>
                                                                    ) : (
                                                                        <span className="text-grey40 dark:text-nightMuted">{request.failedCount}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="text-xs text-grey50 dark:text-nightMuted">
                                                                Created: {new Date(request.createdAt).toLocaleString()}
                                                                {request.completedAt && (
                                                                    <> | Completed: {new Date(request.completedAt).toLocaleString()}</>
                                                                )}
                                                            </div>
                                                            {request.errorMessage && (
                                                                <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                                                                    Error: {request.errorMessage}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-2 flex-wrap sm:ml-4">
                                                            <Button
                                                                size="sm"
                                                                variant="flat"
                                                                color="default"
                                                                className="no-global-button-color h-9 rounded-full bg-grey10 px-4 text-sm font-semibold text-white dark:bg-white dark:text-nightBg"
                                                                onPress={() => {
                                                                    setSelectedEmailForPreview(request);
                                                                    onEmailHistoryPreviewOpen();
                                                                }}
                                                            >
                                                                View Email
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="light"
                                                                className={`no-global-button-color h-9 ${toolBtn}`}
                                                                onPress={() => {
                                                                    setSelectedEmailRequest(request.id);
                                                                    onEmailRecipientsOpen();
                                                                }}
                                                            >
                                                                View Recipients
                                                            </Button>
                                                            {request.failedCount > 0 && (
                                                                <Button
                                                                    size="sm"
                                                                    color="warning"
                                                                    variant="flat"
                                                                    className="no-global-button-color h-9 rounded-full px-4 text-sm font-semibold dark:text-yellow-300"
                                                                    onPress={() => {
                                                                        if (confirm(`Retry ${request.failedCount} failed email(s)?`)) {
                                                                            retryFailedEmailsMutation.mutate(request.id);
                                                                        }
                                                                    }}
                                                                    isLoading={retryFailedEmailsMutation.isPending}
                                                                    isDisabled={retryFailedEmailsMutation.isPending}
                                                                >
                                                                    Retry Failed ({request.failedCount})
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'formBuilder' && (
                        <div className="">
                            {selectedListView && (
                                <div className="flex flex-col lg:flex-row gap-6">
                                    {/* Left Column: Form Builder */}
                                    <div className={`${surface} w-full flex-shrink-0 p-5 lg:w-[220px]`}>
                                        <h3 className={`${sectionHeading} mb-4`}>Form Builder</h3>
                                        <div className="flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible -mx-1 px-1 lg:mx-0 lg:px-0 pb-1 lg:pb-0">
                                            {/* Text Field Component */}
                                            <div
                                                className="flex shrink-0 cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-2xl bg-white p-3 ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10 lg:whitespace-normal"
                                                onClick={() => formGeneratorRef.current?.addQuestion('short-answer')}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <TextFieldIcon />
                                                    <span className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Text Field</span>
                                                </div>

                                            </div>

                                            {/* Text Area Component */}
                                            <div
                                                className="flex shrink-0 cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-2xl bg-white p-3 ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10 lg:whitespace-normal"
                                                onClick={() => formGeneratorRef.current?.addQuestion('long-answer')}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <TextAreaIcon />
                                                    <span className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Text Area</span>
                                                </div>

                                            </div>

                                            {/* Checkbox Component */}
                                            <div
                                                className="flex shrink-0 cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-2xl bg-white p-3 ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10 lg:whitespace-normal"
                                                onClick={() => formGeneratorRef.current?.addQuestion('checkbox')}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <CheckboxIcon />
                                                    <span className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Checkbox</span>
                                                </div>

                                            </div>

                                            {/* Select Component */}
                                            <div
                                                className="flex shrink-0 cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-2xl bg-white p-3 ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10 lg:whitespace-normal"
                                                onClick={() => formGeneratorRef.current?.addQuestion('select')}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <SelectIcon />
                                                    <span className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Select</span>
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Entry Form */}
                                    <div className={`${card} min-w-0 flex-1 p-5 sm:p-6`}>
                                        <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-4">
                                            <div className="flex flex-col gap-2">
                                                <h3 className={`${sectionHeading} mb-2`}>Entry Form</h3>
                                                <p className="mb-6 text-sm text-grey40 dark:text-nightMuted">
                                                    Create a custom event form to collect attendee data and a QR code for easy access.
                                                </p>
                                            </div>
                                            <div className="flex items-end gap-2 flex-col">
                                                {/* Action Buttons */}
                                                <div className="flex gap-2 ">
                                                    {/* <Button
                                                        variant="light"
                                                        size="sm"
                                                        className="border border-[#F4F5F6] rounded"
                                                        startContent={
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                                <circle cx="12" cy="12" r="3"></circle>
                                                            </svg>
                                                        }
                                                        onClick={() => formGeneratorRef.current?.togglePreview()}
                                                    >
                                                        {formGeneratorRef.current?.isPreview ? 'Edit' : 'Preview'}
                                                    </Button> */}
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        className={`${accentBtnSm} px-6`}
                                                        onClick={async () => {
                                                            setIsSavingForm(true);
                                                            try {
                                                                await formGeneratorRef.current?.save();
                                                            } finally {
                                                                setIsSavingForm(false);
                                                            }
                                                        }}
                                                        isLoading={isSavingForm}
                                                        isDisabled={isSavingForm}
                                                    >
                                                        Save
                                                    </Button>
                                                </div>

                                                {/* Share Link & QR Code */}

                                            </div>

                                        </div>

                                        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">

                                            <div className="flex items-start gap-2 flex-col w-full sm:w-auto">
                                                <div className="text-xs font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted">
                                                    QR Code
                                                </div>
                                                <div className="grid grid-cols-3 gap-2 w-full sm:flex sm:w-auto sm:flex-wrap">


                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className={`${toolBtn} w-full sm:w-auto`}
                                                        endContent={
                                                            <DownloadIcon />
                                                        }
                                                        onPress={async () => {
                                                            if (!selectedListView) return;
                                                            try {
                                                                const formUrl = `${window.location.origin}/contact-form/${selectedListView._id}`;

                                                                // Generate QR code directly as data URL for download
                                                                const dataUrl = await QRCode.toDataURL(formUrl, {
                                                                    width: 300,
                                                                    margin: 2,
                                                                    color: {
                                                                        dark: '#000000',
                                                                        light: '#FFFFFF'
                                                                    }
                                                                });

                                                                // Also update the canvas for display
                                                                generateQRCode(`tab-${selectedListView._id}`, selectedListView._id);

                                                                // Create download link
                                                                const link = document.createElement('a');
                                                                link.download = `contact-form-qr-${selectedListView.name.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
                                                                link.href = dataUrl;
                                                                link.style.display = 'none';

                                                                // Append to body and trigger download
                                                                document.body.appendChild(link);
                                                                link.click();

                                                                // Clean up
                                                                setTimeout(() => {
                                                                    if (link.parentNode) {
                                                                        document.body.removeChild(link);
                                                                    }
                                                                }, 100);
                                                            } catch (error) {
                                                                if (!selectedListView) return;
                                                                console.error('Error downloading QR code:', error);
                                                                // Fallback: try to get from canvas
                                                                const canvas = document.getElementById(`qr-tab-${selectedListView._id}`) as HTMLCanvasElement;
                                                                if (canvas && canvas.width > 0 && canvas.height > 0) {
                                                                    const dataUrl = canvas.toDataURL('image/png');
                                                                    const link = document.createElement('a');
                                                                    link.download = `contact-form-qr-${selectedListView.name.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
                                                                    link.href = dataUrl;
                                                                    link.style.display = 'none';
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    setTimeout(() => {
                                                                        if (link.parentNode) {
                                                                            document.body.removeChild(link);
                                                                        }
                                                                    }, 100);
                                                                } else {
                                                                    if (!selectedListView) return;
                                                                    // Generate QR code first, then download
                                                                    generateQRCode(`tab-${selectedListView._id}`, selectedListView._id);
                                                                    setTimeout(() => {
                                                                        if (!selectedListView) return;
                                                                        const retryCanvas = document.getElementById(`qr-tab-${selectedListView._id}`) as HTMLCanvasElement;
                                                                        if (retryCanvas && retryCanvas.width > 0) {
                                                                            const dataUrl = retryCanvas.toDataURL('image/png');
                                                                            const link = document.createElement('a');
                                                                            link.download = `contact-form-qr-${selectedListView.name.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
                                                                            link.href = dataUrl;
                                                                            link.style.display = 'none';
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                            setTimeout(() => {
                                                                                if (link.parentNode) {
                                                                                    document.body.removeChild(link);
                                                                                }
                                                                            }, 100);
                                                                        }
                                                                    }, 500);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        QR
                                                    </Button>
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className={`${toolBtn} w-full sm:w-auto`}
                                                        endContent={
                                                            <PrintIcon />
                                                        }
                                                        onPress={async () => {
                                                            if (!selectedListView || !selectedListView._id) return;

                                                            try {
                                                                const formUrl = `${window.location.origin}/contact-form/${selectedListView._id}`;

                                                                // Fetch organizer/organization data
                                                                let orgLogo = '';
                                                                let orgName = '';
                                                                try {
                                                                    const organizerIdToFetch = selectedOrganizerId || user?.userId;
                                                                    if (organizerIdToFetch) {
                                                                        const organizerData = await fetchOrganizerById(organizerIdToFetch);
                                                                        orgName = organizerData?.orgName || '';
                                                                        orgLogo = organizerData?.attachments?.avatar?.link || organizerData?.avatar || '';
                                                                    }
                                                                } catch (orgError) {
                                                                    console.warn('Could not fetch organizer data:', orgError);
                                                                }

                                                                // Generate QR code
                                                                const qrDataUrl = await QRCode.toDataURL(formUrl, {
                                                                    width: 1200,
                                                                    margin: 2,
                                                                    color: { dark: '#000000', light: '#FFFFFF' }
                                                                });

                                                                // Create print window
                                                                const printWindow = window.open('', '_blank');
                                                                if (!printWindow) {
                                                                    addToast({
                                                                        title: 'Error',
                                                                        description: 'Please allow popups to print',
                                                                        color: 'danger',
                                                                    });
                                                                    return;
                                                                }

                                                                // Build print HTML
                                                                let printHTML = `
                                                                    <!DOCTYPE html>
                                                                    <html>
                                                                    <head>
                                                                        <title>Contact Form QR Code</title>
                                                                        <style>
                                                                            @media print {
                                                                                @page {
                                                                                    size: letter;
                                                                                    margin: 10mm;
                                                                                }
                                                                                body {
                                                                                    margin: 0;
                                                                                    padding: 0;
                                                                                }
                                                                            }
                                                                            body {
                                                                                font-family: Arial, sans-serif;
                                                                                display: flex;
                                                                                flex-direction: column;
                                                                                align-items: center;
                                                                                justify-content: center;
                                                                                padding: 10mm;
                                                                                background: white;
                                                                                
                                                                                page-break-inside: avoid;
                                                                            }
                                                                            .org-header {
                                                                                display: flex;
                                                                                flex-direction: row;
                                                                                align-items: center;
                                                                                justify-content: center;
                                                                                gap: 12px;
                                                                                margin-bottom: 15px;
                                                                                flex-shrink: 0;
                                                                            }
                                                                            .org-logo {
                                                                                width: 100px;
                                                                                height: 100px;
                                                                                object-fit: contain;
                                                                                border-radius: 8px;
                                                                                flex-shrink: 0;
                                                                            }
                                                                            .org-name {
                                                                                font-size: 24px;
                                                                                font-weight: bold;
                                                                                text-align: center;
                                                                                color: #000000;
                                                                                flex-shrink: 0;
                                                                            }
                                                                            .list-name {
                                                                                font-size: 24px;
                                                                                font-weight: bold;
                                                                                text-align: center;
                                                                                color: #000000;
                                                                                width: 100%;
                                                                                margin-bottom: 20px;
                                                                                flex-shrink: 0;
                                                                            }
                                                                            .qr-code {
                                                                                width: 100%;
                                                                                max-width: 500px;
                                                                                height: auto;
                                                                                display: block;
                                                                                flex-shrink: 0;
                                                                            }
                                                                        </style>
                                                                    </head>
                                                                    <body>
                                                                `;

                                                                // Add organization header
                                                                if (orgLogo || orgName) {
                                                                    printHTML += '<div class="org-header">';
                                                                    if (orgLogo) {
                                                                        printHTML += `<img src="${orgLogo}" class="org-logo" alt="Organization Logo" />`;
                                                                    }
                                                                    if (orgName) {
                                                                        printHTML += `<div class="org-name">${orgName}</div>`;
                                                                    }
                                                                    printHTML += '</div>';
                                                                }

                                                                // Add list name
                                                                printHTML += `<div class="list-name">${selectedListView.name}</div>`;

                                                                // Add QR code
                                                                printHTML += `<img src="${qrDataUrl}" class="qr-code" alt="QR Code" />`;

                                                                printHTML += `
                                                                    </body>
                                                                    </html>
                                                                `;

                                                                // Write to print window
                                                                printWindow.document.write(printHTML);
                                                                printWindow.document.close();

                                                                // Wait for images to load, then print
                                                                printWindow.onload = () => {
                                                                    setTimeout(() => {
                                                                        printWindow.print();
                                                                        // Close window after printing (optional)
                                                                        // printWindow.close();
                                                                    }, 500);
                                                                };

                                                                addToast({
                                                                    title: 'Print',
                                                                    description: 'Opening print dialog...',
                                                                    color: 'success',
                                                                });
                                                            } catch (error) {
                                                                console.error('Error printing:', error);
                                                                addToast({
                                                                    title: 'Error',
                                                                    description: error instanceof Error ? error.message : 'Failed to print',
                                                                    color: 'danger',
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        QR
                                                    </Button>
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className={`${toolBtn} w-full sm:w-auto sm:min-w-[110px]`}
                                                        endContent={
                                                            <CopyIcon />
                                                        }
                                                        onPress={() => {
                                                            addToast({
                                                                title: 'Link copied to clipboard',
                                                                description: 'The link has been copied to your clipboard',
                                                                color: 'success',
                                                            });
                                                            navigator.clipboard.writeText(`${window.location.origin}/contact-form/${selectedListView._id}`);

                                                        }}
                                                    >
                                                        Form Link
                                                    </Button>
                                                </div>


                                            </div>
                                            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-4">
                                                <div className='flex items-center gap-2'>
                                                    <Button
                                                        variant="light"

                                                        isIconOnly
                                                        className='w-fit h-[38px] rounded-full dark:ring-1 dark:ring-white/40'
                                                        onPress={async () => {
                                                            try {
                                                                const organizerIdToUse = selectedOrganizerId || user?.userId;
                                                                const passBlob = await downloadContactListAppleWalletPass(selectedListView._id, organizerIdToUse || undefined);
                                                                const downloadUrl = window.URL.createObjectURL(passBlob);
                                                                const link = document.createElement('a');
                                                                link.href = downloadUrl;
                                                                link.download = `contact-list-${selectedListView._id}.pkpass`;
                                                                link.style.display = 'none';
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                document.body.removeChild(link);
                                                                window.URL.revokeObjectURL(downloadUrl);
                                                                addToast({
                                                                    title: 'Apple Wallet pass downloaded',
                                                                    description: 'The pass has been downloaded. Open it to add to Apple Wallet.',
                                                                    color: 'success',
                                                                });
                                                            } catch (error) {
                                                                console.error('Error downloading Apple Wallet pass:', error);
                                                                addToast({
                                                                    title: 'Error',
                                                                    description: error instanceof Error ? error.message : 'Failed to download Apple Wallet pass',
                                                                    color: 'danger',
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <AddToWalletApple />
                                                    </Button>


                                                    <Button
                                                        variant="light"
                                                        isIconOnly
                                                        className='w-fit h-[38px] rounded-full dark:ring-1 dark:ring-white/40'
                                                        onPress={async () => {
                                                            try {
                                                                const organizerIdToUse = selectedOrganizerId || user?.userId;
                                                                const saveUrl = await downloadContactListGoogleWalletPass(selectedListView._id, organizerIdToUse || undefined);
                                                                // Open Google Wallet save URL
                                                                window.open(saveUrl, '_blank');
                                                                addToast({
                                                                    title: 'Google Wallet',
                                                                    description: 'Opening Google Wallet to add pass...',
                                                                    color: 'success',
                                                                });
                                                            } catch (error) {
                                                                console.error('Error downloading Google Wallet pass:', error);
                                                                addToast({
                                                                    title: 'Error',
                                                                    description: error instanceof Error ? error.message : 'Failed to open Google Wallet',
                                                                    color: 'danger',
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <AddToWalletGoogle />
                                                    </Button>
                                                </div>
                                                <canvas
                                                    id={`qr-tab-${selectedListView._id}`}
                                                    className="w-[84px] h-[84px] rounded-xl bg-white ring-1 ring-black/[0.06] dark:ring-white/10"
                                                />
                                            </div>
                                        </div>


                                        {/* Form Generator - Edit Form section */}
                                        <div className="border-t border-black/[0.06] pt-6 dark:border-white/10">
                                            <FormGenerator
                                                ref={formGeneratorRef}
                                                organizerId={selectedListView._id}
                                                onSave={handleSaveForm}
                                                hideAddQuestions={true}
                                                initialData={selectedListView.formQuestions && selectedListView.formQuestions.length > 0 ? {
                                                    id: selectedListView._id,
                                                    title: selectedListView.formTitle || 'Contact Form',
                                                    description: selectedListView.formDescription || '',
                                                    questions: selectedListView.formQuestions,
                                                    organizerId: selectedListView._id
                                                } : undefined}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : null}

            {/* Add Contact Modal */}
            <Modal
                isOpen={isAddContactOpen}
                onClose={onAddContactClose}
                size={isMobile ? 'full' : 'md'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                    body: "max-h-[60vh] overflow-y-auto"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Add New Contact
                    </ModalHeader>
                    <ModalBody>
                        <form onSubmit={handleAddContact} className="space-y-4">
                            <Input
                                classNames={inputCls}
                                label="Name"
                                placeholder="Enter first name"
                                value={newContact.name}
                                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                                isRequired
                            />
                            <Input
                                classNames={inputCls}
                                label="Last Name"
                                placeholder="Enter last name"
                                value={newContact.lastName}
                                onChange={(e) => setNewContact(prev => ({ ...prev, lastName: e.target.value }))}
                                isRequired
                            />
                            <div>
                                <Input
                                    classNames={inputCls}
                                    label="Email"
                                    placeholder="Enter email address"
                                    type="email"
                                    value={newContact.email}
                                    onChange={handleEmailChange}
                                    onBlur={handleEmailBlur}
                                    isRequired
                                    isInvalid={!!emailError}
                                    errorMessage={emailError}
                                />
                            </div>
                            <Input
                                classNames={inputCls}
                                label="Phone"
                                placeholder="Enter phone number"
                                type="tel"
                                value={newContact.phone}
                                onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                            />
                            <div>
                                <label className={labelCls}>
                                    List <span className="text-red-500">*</span>
                                </label>
                                {selectedListView ? (
                                    <div className={`${nativeField} flex h-14 items-center`}>
                                        {selectedListView.name}
                                    </div>
                                ) : (
                                    <>
                                        <Select aria-label="Select" selectedKeys={[String(selectedListForNewContact ?? '')]} onChange={(e) => setSelectedListForNewContact(e.target.value)} isRequired>
                                            <SelectItem key="">Select a list</SelectItem>
                                            {contactLists.map((list: ContactList) => (
                                                <SelectItem key={String(list._id)}>{list.name}</SelectItem>
                                            ))}
                                        </Select>
                                        {!selectedListForNewContact && (
                                            <p className="text-xs text-red-500 mt-1">Please select a list</p>
                                        )}
                                    </>
                                )}
                            </div>
                            {/*  <div>
                                <Input
                                    classNames={inputCls}
                                    label="Tags"
                                    placeholder="e.g Music, Theatre, Comedy"
                                    onKeyDown={handleTagInput}
                                />
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {newContact.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className={`${pill} gap-1 bg-primary text-white dark:bg-nightAccent dark:text-nightBg`}
                                        >
                                            #{tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(tag)}
                                                className="ml-1 text-white/80 hover:text-white"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div> */}

                            {/* Form Questions */}
                            {isLoadingFormQuestions ? (
                                <div className="space-y-4 border-t border-black/[0.06] pt-4 dark:border-white/10">
                                    <div className="flex items-center justify-center py-8">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary dark:border-nightAccent"></div>
                                            <p className="text-sm text-grey40 dark:text-nightMuted">Loading form questions...</p>
                                        </div>
                                    </div>
                                </div>
                            ) : selectedListFormQuestions.length > 0 ? (
                                <div className="space-y-4 border-t border-black/[0.06] pt-4 dark:border-white/10">
                                    <h3 className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Additional Information</h3>
                                    {selectedListFormQuestions.map((question) => (
                                        <div key={question.id} className={`${surface} p-5`}>
                                            {renderFormQuestion(question)}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </form>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onAddContactClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={accentBtnSm}
                            color="primary"
                            onPress={handleAddContact}
                            isDisabled={!selectedListForNewContact || !newContact.name || !newContact.email || !!emailError || !validateEmail(newContact.email)}
                        >
                            Create Contact
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* New List Modal */}
            <Modal
                isOpen={isNewListOpen}
                onClose={onNewListClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Create New List
                    </ModalHeader>
                    <ModalBody>
                        <form onSubmit={handleCreateList} className="space-y-4">
                            <Input
                                classNames={inputCls}
                                label="List Name"
                                placeholder="Enter list name"
                                value={newList.name}
                                onChange={(e) => setNewList(prev => ({ ...prev, name: e.target.value }))}
                                isRequired
                            />
                            <Textarea
                                classNames={textareaCls}
                                label="Description"
                                placeholder="Enter list description"
                                value={newList.description}
                                onChange={(e) => setNewList(prev => ({ ...prev, description: e.target.value }))}
                                rows={3}
                            />
                        </form>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onNewListClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={accentBtnSm}
                            color="primary"
                            onPress={handleCreateList}
                        >
                            Create List
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Add to List Modal */}
            <Modal
                isOpen={isAddToListOpen}
                onClose={onAddToListClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Add to List
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div>
                                <label className={labelCls}>Select List</label>
                                <Select aria-label="Select" selectedKeys={[String(addToList ?? '')]} onChange={(e) => setAddToList(e.target.value)}>
                                    <SelectItem key="">Select a list</SelectItem>
                                    {contactLists.map(list => (
                                        <SelectItem key={String(list._id)}>{list.name}</SelectItem>
                                    ))}
                                </Select>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="h-px flex-1 bg-black/[0.06] dark:bg-white/10"></div>
                                <span className="text-sm text-grey40 dark:text-nightMuted">or</span>
                                <div className="h-px flex-1 bg-black/[0.06] dark:bg-white/10"></div>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Create New List</span>
                                <button
                                    onClick={() => setShowNewListInAdd(!showNewListInAdd)}
                                    className="text-sm font-semibold text-primary hover:text-primary30 dark:text-nightAccent"
                                >
                                    {showNewListInAdd ? 'Cancel' : 'New List +'}
                                </button>
                            </div>

                            {showNewListInAdd && (
                                <div className={`${surface} space-y-3 p-5`}>
                                    <Input
                                        classNames={inputCls}
                                        label="List Name"
                                        placeholder="Enter new list name"
                                        value={newListInAdd.name}
                                        onChange={(e) => setNewListInAdd(prev => ({ ...prev, name: e.target.value }))}
                                        isRequired
                                    />
                                    <Textarea
                                        classNames={textareaCls}
                                        label="Description"
                                        placeholder="Enter list description (optional)"
                                        value={newListInAdd.description}
                                        onChange={(e) => setNewListInAdd(prev => ({ ...prev, description: e.target.value }))}
                                        rows={2}
                                    />
                                </div>
                            )}

                            <div className="text-sm text-grey40 dark:text-nightMuted">
                                Moving {selectedContacts.length} contact(s) to the selected list. Each contact can only be in one list.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onAddToListClose}
                        >
                            Cancel
                        </Button>
                        {showNewListInAdd ? (
                            <Button
                                className={accentBtnSm}
                                color="primary"
                                onPress={handleCreateListAndAdd}
                                isDisabled={!newListInAdd.name}
                                isLoading={createListAndAddContactsMutation.isPending}
                            >
                                {createListAndAddContactsMutation.isPending ? 'Creating...' : 'Create & Add'}
                            </Button>
                        ) : (
                            <Button
                                className={accentBtnSm}
                                color="primary"
                                onPress={handleAddToList}
                                isDisabled={!addToList}
                            >
                                Add to List
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Remove from List Modal */}
            <Modal
                isOpen={isRemoveFromListOpen}
                onClose={onRemoveFromListClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Remove from List
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            {selectedListView ? (
                                <div>
                                    <label className={labelCls}>List</label>
                                    <Input
                                        classNames={inputCls}
                                        readOnly
                                        value={selectedListView.name}
                                        className="w-full"
                                    />
                                    <p className="mt-1 text-xs text-grey50 dark:text-nightMuted">
                                        Contacts will be removed from: {selectedListView.name}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <label className={labelCls}>Select List</label>
                                    <Select aria-label="Select" selectedKeys={[String(removeFromList ?? '')]} onChange={(e) => setRemoveFromList(e.target.value)}>
                                        <SelectItem key="">Select a list</SelectItem>
                                        {contactLists.map((list: ContactList) => (
                                            <SelectItem key={String(list._id)}>{list.name}</SelectItem>
                                        ))}
                                    </Select>
                                </div>
                            )}
                            <div className="text-sm text-grey40 dark:text-nightMuted">
                                Removing {selectedContacts.length} contact(s) from the selected list.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onRemoveFromListClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="h-10 rounded-full px-5 text-sm font-semibold"
                            color="warning"
                            onPress={handleRemoveFromList}
                            isDisabled={!(selectedListView?._id || removeFromList)}
                        >
                            Remove from List
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Send Email Modal */}
            <Modal
                isOpen={isSendEmailOpen}
                onClose={onSendEmailClose}
                size={isMobile ? 'full' : '2xl'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Send Email to List
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            {/* Top 4 fields in 2 rows */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Row 1: List and Subject */}
                                {selectedListView ? (
                                    <div>
                                        <label className={labelCls}>
                                            List
                                        </label>
                                        <div className={`${nativeField} flex h-14 items-center`}>
                                            {selectedListView.name}
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label className={labelCls}>Select List</label>
                                        <Select aria-label="Select" selectedKeys={[String(emailData.listId ?? '')]} onChange={(e) => setEmailData(prev => ({ ...prev, listId: e.target.value }))}>
                                            <SelectItem key="">Select a list</SelectItem>
                                            {contactLists.map((list: ContactList) => (
                                                <SelectItem key={String(list._id)}>{list.name}</SelectItem>
                                            ))}
                                        </Select>
                                    </div>
                                )}
                                <Input
                                    classNames={inputCls}
                                    label="Subject"
                                    placeholder="Enter email subject"
                                    value={emailData.subject}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                                    isRequired
                                />
                                {/* Row 2: Sender Name and Reply-To Email */}
                                <div>
                                    <Input
                                        classNames={inputCls}
                                        label="Sender Name"
                                        placeholder="e.g., Eventup (optional)"
                                        value={emailData.emailName}
                                        onChange={(e) => setEmailData(prev => ({ ...prev, emailName: e.target.value }))}
                                    />
                                    <p className="mt-1 text-xs text-grey50 dark:text-nightMuted">
                                        The name that will appear as the sender. Defaults to 'Eventup' if not provided.
                                    </p>
                                </div>
                                <div>
                                    <Input
                                        classNames={inputCls}
                                        label="Reply-To Email"
                                        placeholder="e.g., support@example.com (optional)"
                                        value={emailData.replyToEmail}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setEmailData(prev => ({ ...prev, replyToEmail: value }));
                                            // Validate email on change (empty is valid since it's optional)
                                            if (value && !validateEmail(value)) {
                                                setReplyToEmailError('Please enter a valid email address');
                                            } else {
                                                setReplyToEmailError('');
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Validate on blur as well (empty is valid since it's optional)
                                            const value = e.target.value;
                                            if (value && !validateEmail(value)) {
                                                setReplyToEmailError('Please enter a valid email address');
                                            } else {
                                                setReplyToEmailError('');
                                            }
                                        }}
                                        errorMessage={replyToEmailError}
                                        isInvalid={!!replyToEmailError}
                                        type="email"
                                    />
                                    <p className="mt-1 text-xs text-grey50 dark:text-nightMuted">
                                        Email address for replies. Defaults to 'admin@alimotech.com' if not provided.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>
                                    Message
                                </label>
                                <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10">
                                    <div className="border-b border-black/[0.06] bg-grey99 px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]">
                                        <div className="flex gap-1.5 flex-wrap items-center">
                                            {/* Text Formatting Group */}
                                            <div className="flex gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('bold');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className={`rounded-full px-2 py-1 text-xs font-semibold transition-all ${emailEditorState.isBold
                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                        : 'text-grey20 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10'
                                                        }`}
                                                    title="Bold"
                                                >
                                                    B
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('italic');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className={`rounded-full px-2 py-1 text-xs italic transition-all ${emailEditorState.isItalic
                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                        : 'text-grey20 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10'
                                                        }`}
                                                    title="Italic"
                                                >
                                                    I
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('strikeThrough');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className={`rounded-full px-2 py-1 text-xs transition-all ${emailEditorState.isStrike
                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                        : 'text-grey20 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10'
                                                        }`}
                                                    title="Strikethrough"
                                                >
                                                    <s>S</s>
                                                </button>
                                            </div>

                                            {/* Headings Group */}
                                            <div className="flex gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('formatBlock', '<h1>');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs font-bold text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Heading 1"
                                                >
                                                    H1
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('formatBlock', '<h2>');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs font-semibold text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Heading 2"
                                                >
                                                    H2
                                                </button>
                                            </div>

                                            {/* Lists Group */}
                                            <div className="flex gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('insertUnorderedList');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Bullet List"
                                                >
                                                    •
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('insertOrderedList');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Numbered List"
                                                >
                                                    1.
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execCommand('formatBlock', '<blockquote>');
                                                        setTimeout(() => checkFormatting(emailEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Quote"
                                                >
                                                    "
                                                </button>
                                            </div>

                                            {/* Link */}
                                            <button
                                                type="button"
                                                onClick={() => insertLink(emailEditorRef)}
                                                className="rounded-full bg-white px-2.5 py-1 text-xs text-grey20 ring-1 ring-black/[0.06] transition-all hover:bg-grey99 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10"
                                                title="Insert Link"
                                            >
                                                🔗
                                            </button>

                                            {/* Font Controls */}
                                            <div className="flex items-center gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <Select classNames={{ trigger: 'h-10 px-3 text-sm' }} aria-label="Select" selectedKeys={[String(emailEditorState.fontSize ?? '')]} onChange={(e) => {
                                                        setFontSize(e.target.value, emailEditorRef);
                                                        setEmailEditorState(prev => ({ ...prev, fontSize: e.target.value }));
                                                    }}>
                                                    <SelectItem key="10px">10</SelectItem>
                                                    <SelectItem key="12px">12</SelectItem>
                                                    <SelectItem key="14px">14</SelectItem>
                                                    <SelectItem key="16px">16</SelectItem>
                                                    <SelectItem key="18px">18</SelectItem>
                                                    <SelectItem key="20px">20</SelectItem>
                                                    <SelectItem key="24px">24</SelectItem>
                                                    <SelectItem key="28px">28</SelectItem>
                                                    <SelectItem key="32px">32</SelectItem>
                                                    <SelectItem key="36px">36</SelectItem>
                                                </Select>
                                                <div className="h-4 w-px bg-grey90 dark:bg-white/15"></div>
                                                <input
                                                    type="color"
                                                    value={emailEditorState.fontColor}
                                                    onChange={(e) => {
                                                        setFontColor(e.target.value, emailEditorRef);
                                                        setEmailEditorState(prev => ({ ...prev, fontColor: e.target.value }));
                                                    }}
                                                    className="h-6 w-6 cursor-pointer rounded-full ring-1 ring-black/10 transition-shadow hover:ring-2 dark:ring-white/20"
                                                    title="Font Color"
                                                />
                                            </div>

                                            {/* Image */}
                                            <button
                                                type="button"
                                                onClick={() => emailImageInputRef.current?.click()}
                                                className="rounded-full bg-white px-2.5 py-1 text-xs text-grey20 ring-1 ring-black/[0.06] transition-all hover:bg-grey99 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10"
                                                title="Insert Image"
                                            >
                                                📷
                                            </button>
                                        </div>
                                        <input
                                            ref={emailImageInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleEmailImageUpload}
                                            className="hidden"
                                        />
                                    </div>
                                    <div
                                        ref={emailEditorRef}
                                        contentEditable
                                        onInput={handleEmailEditorChange}
                                        onMouseUp={() => checkFormatting(emailEditorRef)}
                                        onKeyUp={() => checkFormatting(emailEditorRef)}
                                        className="prose prose-sm min-h-[200px] max-w-none bg-white p-4 focus:outline-none dark:prose-invert dark:bg-transparent dark:text-nightTextSecondary"
                                        style={{
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            overflowX: 'hidden',
                                            overflowY: 'auto',
                                            maxHeight: '400px',
                                            boxSizing: 'border-box',
                                            lineHeight: '1.5'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="text-sm text-grey40 dark:text-nightMuted">
                                This email will be sent to all contacts in the selected list.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={onSendEmailClose}
                            className={`no-global-button-color ${toolBtn}`}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="flat"
                            color="default"
                            onPress={onEmailPreviewOpen}
                            isDisabled={!emailData.subject || !emailData.message}
                            className="no-global-button-color h-10 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey10 dark:bg-white/[0.06] dark:text-white"
                        >
                            Preview
                        </Button>
                        <Button
                            className={`${accentBtnSm} px-6`}
                            onPress={handleSendEmail}
                            isDisabled={!emailData.subject || !emailData.message || (!emailData.listId && !selectedListView?._id) || !!replyToEmailError}
                            isLoading={sendEmailToListMutation.isPending}
                        >
                            {sendEmailToListMutation.isPending ? 'Sending...' : 'Send Email'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Email Preview Modal */}
            <Modal
                isOpen={isEmailPreviewOpen}
                onClose={onEmailPreviewClose}
                size={isMobile ? 'full' : '5xl'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Email Preview
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div className="mb-2 text-sm text-grey40 dark:text-nightMuted">
                                <strong>Subject:</strong> {emailData.subject || '(No subject)'}
                            </div>
                            <div className="overflow-hidden rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/10">
                                <iframe
                                    srcDoc={generateEmailPreview()}
                                    className="w-full h-[600px] border-0"
                                    title="Email Preview"
                                />
                            </div>
                            <div className="text-xs italic text-grey50 dark:text-nightMuted">
                                This is how your email will appear to recipients. Note: Some email clients may render styles differently.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onEmailPreviewClose}
                        >
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Send Notification Modal */}
            <Modal
                isOpen={isSendNotificationOpen}
                onClose={onSendNotificationClose}
                size={isMobile ? 'full' : 'lg'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Send Notification to List
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div>
                                <label className={labelCls}>Select List</label>
                                <Select aria-label="Select" selectedKeys={[String(notificationData.listId ?? '')]} onChange={(e) => setNotificationData(prev => ({ ...prev, listId: e.target.value }))}>
                                    <SelectItem key="">Select a list</SelectItem>
                                    {contactLists.map(list => (
                                        <SelectItem key={String(list._id)}>{list.name}</SelectItem>
                                    ))}
                                </Select>
                            </div>
                            <Input
                                classNames={inputCls}
                                label="Title"
                                placeholder="Enter notification title"
                                value={notificationData.title}
                                onChange={(e) => setNotificationData(prev => ({ ...prev, title: e.target.value }))}
                                isRequired
                            />
                            <div>
                                <label className={labelCls}>
                                    Message
                                </label>
                                <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10">
                                    <div className="border-b border-black/[0.06] bg-grey99 px-2 py-1.5 dark:border-white/10 dark:bg-white/[0.04]">
                                        <div className="flex gap-1 flex-wrap items-center">
                                            {/* Text Formatting Group */}
                                            <div className="flex gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('bold');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className={`rounded-full px-2 py-1 text-xs font-semibold transition-all ${notificationEditorState.isBold
                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                        : 'text-grey20 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10'
                                                        }`}
                                                    title="Bold"
                                                >
                                                    B
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('italic');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className={`rounded-full px-2 py-1 text-xs italic transition-all ${notificationEditorState.isItalic
                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                        : 'text-grey20 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10'
                                                        }`}
                                                    title="Italic"
                                                >
                                                    I
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('strikeThrough');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className={`rounded-full px-2 py-1 text-xs transition-all ${notificationEditorState.isStrike
                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                        : 'text-grey20 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10'
                                                        }`}
                                                    title="Strikethrough"
                                                >
                                                    <s>S</s>
                                                </button>
                                            </div>

                                            {/* Headings Group */}
                                            <div className="flex gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('formatBlock', '<h1>');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs font-bold text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Heading 1"
                                                >
                                                    H1
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('formatBlock', '<h2>');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs font-semibold text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Heading 2"
                                                >
                                                    H2
                                                </button>
                                            </div>

                                            {/* Lists Group */}
                                            <div className="flex gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('insertUnorderedList');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Bullet List"
                                                >
                                                    •
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        execNotificationCommand('insertOrderedList');
                                                        setTimeout(() => checkFormatting(notificationEditorRef), 10);
                                                    }}
                                                    className="rounded-full px-2 py-1 text-xs text-grey20 transition-all hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                    title="Numbered List"
                                                >
                                                    1.
                                                </button>
                                            </div>

                                            {/* Link */}
                                            <button
                                                type="button"
                                                onClick={() => insertLink(notificationEditorRef)}
                                                className="rounded-full bg-white px-2.5 py-1 text-xs text-grey20 ring-1 ring-black/[0.06] transition-all hover:bg-grey99 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10"
                                                title="Insert Link"
                                            >
                                                🔗
                                            </button>

                                            {/* Font Controls */}
                                            <div className="flex items-center gap-0.5 rounded-full bg-white p-0.5 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                <Select classNames={{ trigger: 'h-10 px-3 text-sm' }} aria-label="Select" selectedKeys={[String(notificationEditorState.fontSize ?? '')]} onChange={(e) => {
                                                        setFontSize(e.target.value, notificationEditorRef);
                                                        setNotificationEditorState(prev => ({ ...prev, fontSize: e.target.value }));
                                                    }}>
                                                    <SelectItem key="10px">10</SelectItem>
                                                    <SelectItem key="12px">12</SelectItem>
                                                    <SelectItem key="14px">14</SelectItem>
                                                    <SelectItem key="16px">16</SelectItem>
                                                    <SelectItem key="18px">18</SelectItem>
                                                    <SelectItem key="20px">20</SelectItem>
                                                    <SelectItem key="24px">24</SelectItem>
                                                    <SelectItem key="28px">28</SelectItem>
                                                    <SelectItem key="32px">32</SelectItem>
                                                    <SelectItem key="36px">36</SelectItem>
                                                </Select>
                                                <div className="h-4 w-px bg-grey90 dark:bg-white/15"></div>
                                                <input
                                                    type="color"
                                                    value={notificationEditorState.fontColor}
                                                    onChange={(e) => {
                                                        setFontColor(e.target.value, notificationEditorRef);
                                                        setNotificationEditorState(prev => ({ ...prev, fontColor: e.target.value }));
                                                    }}
                                                    className="h-6 w-6 cursor-pointer rounded-full ring-1 ring-black/10 transition-shadow hover:ring-2 dark:ring-white/20"
                                                    title="Font Color"
                                                />
                                            </div>

                                            {/* Image */}
                                            <button
                                                type="button"
                                                onClick={() => notificationImageInputRef.current?.click()}
                                                className="rounded-full bg-white px-2.5 py-1 text-xs text-grey20 ring-1 ring-black/[0.06] transition-all hover:bg-grey99 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10"
                                                title="Insert Image"
                                            >
                                                📷
                                            </button>
                                        </div>
                                        <input
                                            ref={notificationImageInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleNotificationImageUpload}
                                            className="hidden"
                                        />
                                    </div>
                                    <div
                                        ref={notificationEditorRef}
                                        contentEditable
                                        onInput={handleNotificationEditorChange}
                                        onMouseUp={() => checkFormatting(notificationEditorRef)}
                                        onKeyUp={() => checkFormatting(notificationEditorRef)}
                                        className="prose prose-sm min-h-[150px] max-w-none bg-white p-4 focus:outline-none dark:prose-invert dark:bg-transparent dark:text-nightTextSecondary"
                                        style={{
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            overflowX: 'hidden',
                                            overflowY: 'auto',
                                            maxHeight: '400px',
                                            boxSizing: 'border-box',
                                            lineHeight: '1.5'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="text-sm text-grey40 dark:text-nightMuted">
                                This notification will be sent to all contacts in the selected list.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onSendNotificationClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="h-10 rounded-full px-5 text-sm font-semibold"
                            color="success"
                            onPress={handleSendNotification}
                            isDisabled={!notificationData.title || !notificationData.message || !notificationData.listId}
                            isLoading={sendNotificationToListMutation.isPending}
                        >
                            {sendNotificationToListMutation.isPending ? 'Sending...' : 'Send Notification'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Image Settings Modal */}
            <Modal
                isOpen={isImageSettingsOpen}
                onClose={onImageSettingsClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Image Settings
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            {selectedImage && (
                                <div className="flex justify-center mb-4">
                                    <img
                                        src={selectedImage.element.src}
                                        alt="Preview"
                                        className="h-auto max-w-full rounded-2xl ring-1 ring-black/[0.06] dark:ring-white/10"
                                        style={{ maxHeight: '200px' }}
                                    />
                                </div>
                            )}
                            <div>
                                <label className={labelCls}>
                                    Link URL (optional)
                                </label>
                                <Input
                                    classNames={inputCls}
                                    placeholder="https://example.com"
                                    value={imageLinkUrl}
                                    onChange={(e) => setImageLinkUrl(e.target.value)}
                                    description="Leave empty to remove link. Click image with Ctrl/Cmd to open link."
                                />
                            </div>
                            <div>
                                <label className={labelCls}>
                                    Alignment
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (selectedImage) {
                                                setImageLink(selectedImage.element, imageLinkUrl);
                                                alignImage('left');
                                                setCurrentImageAlignment('left');
                                            }
                                            onImageSettingsClose();
                                        }}
                                        className={`flex-1 rounded-2xl px-4 py-3 ring-1 transition-colors ${currentImageAlignment === 'left'
                                            ? 'bg-primary/10 text-primary ring-2 ring-primary dark:bg-nightAccent/15 dark:text-nightAccent dark:ring-nightAccent'
                                            : 'bg-grey99 text-grey20 ring-black/[0.04] hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10'
                                            }`}
                                    >
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-2xl">⬅️</span>
                                            <span className="text-sm">Left</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (selectedImage) {
                                                setImageLink(selectedImage.element, imageLinkUrl);
                                                alignImage('center');
                                                setCurrentImageAlignment('center');
                                            }
                                            onImageSettingsClose();
                                        }}
                                        className={`flex-1 rounded-2xl px-4 py-3 ring-1 transition-colors ${currentImageAlignment === 'center'
                                            ? 'bg-primary/10 text-primary ring-2 ring-primary dark:bg-nightAccent/15 dark:text-nightAccent dark:ring-nightAccent'
                                            : 'bg-grey99 text-grey20 ring-black/[0.04] hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10'
                                            }`}
                                    >
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-2xl">⬆️</span>
                                            <span className="text-sm">Center</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (selectedImage) {
                                                setImageLink(selectedImage.element, imageLinkUrl);
                                                alignImage('right');
                                                setCurrentImageAlignment('right');
                                            }
                                            onImageSettingsClose();
                                        }}
                                        className={`flex-1 rounded-2xl px-4 py-3 ring-1 transition-colors ${currentImageAlignment === 'right'
                                            ? 'bg-primary/10 text-primary ring-2 ring-primary dark:bg-nightAccent/15 dark:text-nightAccent dark:ring-nightAccent'
                                            : 'bg-grey99 text-grey20 ring-black/[0.04] hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10'
                                            }`}
                                    >
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-2xl">➡️</span>
                                            <span className="text-sm">Right</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={() => {
                                if (selectedImage) {
                                    setImageLink(selectedImage.element, imageLinkUrl);
                                    const editorRef = selectedImage.editorRef;
                                    if (editorRef === emailEditorRef) {
                                        handleEmailEditorChange();
                                    } else if (editorRef === notificationEditorRef) {
                                        handleNotificationEditorChange();
                                    }
                                }
                                onImageSettingsClose();
                            }}
                        >
                            Save & Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Manage Lists Modal */}
            {/*  */}

            {/* Edit List Modal */}
            <Modal
                isOpen={isEditListOpen}
                onClose={onEditListClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Edit Contact List
                    </ModalHeader>
                    <ModalBody>
                        <form onSubmit={handleUpdateList} className="space-y-4">
                            <Input
                                classNames={inputCls}
                                label="List Name"
                                placeholder="Enter list name"
                                value={editList.name}
                                onChange={(e) => setEditList(prev => ({ ...prev, name: e.target.value }))}
                                isRequired
                            />
                            <Textarea
                                classNames={textareaCls}
                                label="Description"
                                placeholder="Enter list description"
                                value={editList.description}
                                onChange={(e) => setEditList(prev => ({ ...prev, description: e.target.value }))}
                                rows={3}
                            />
                        </form>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onEditListClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={accentBtnSm}
                            color="primary"
                            onPress={handleUpdateList}
                        >
                            Update List
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Edit Contact Modal */}
            <Modal
                isOpen={isEditContactOpen}
                onClose={onEditContactClose}
                size={isMobile ? 'full' : 'md'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                    body: "max-h-[60vh] overflow-y-auto"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Edit Contact
                    </ModalHeader>
                    <ModalBody>
                        <form onSubmit={handleUpdateContact} className="space-y-4">
                            <Input
                                classNames={inputCls}
                                label="Name"
                                placeholder="Enter first name"
                                value={editContact.name}
                                onChange={(e) => setEditContact(prev => ({ ...prev, name: e.target.value }))}
                                isRequired
                            />
                            <Input
                                classNames={inputCls}
                                label="Last Name"
                                placeholder="Enter last name"
                                value={editContact.lastName}
                                onChange={(e) => setEditContact(prev => ({ ...prev, lastName: e.target.value }))}
                                isRequired
                            />
                            <div>
                                <Input
                                    classNames={inputCls}
                                    label="Email"
                                    placeholder="Enter email address"
                                    type="email"
                                    value={editContact.email}
                                    onChange={handleEditEmailChange}
                                    onBlur={handleEditEmailBlur}
                                    isRequired
                                    isInvalid={!!editEmailError}
                                    errorMessage={editEmailError}
                                />
                            </div>
                            <Input
                                classNames={inputCls}
                                label="Phone"
                                placeholder="Enter phone number"
                                type="tel"
                                value={editContact.phone}
                                onChange={(e) => setEditContact(prev => ({ ...prev, phone: e.target.value }))}
                                isRequired
                            />
                            <div>
                                <Input
                                    classNames={inputCls}
                                    label="Tags"
                                    placeholder="e.g Music, Theatre, Comedy"
                                    onKeyDown={handleEditTagInput}
                                />
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {editContact.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className={`${pill} gap-1 bg-primary text-white dark:bg-nightAccent dark:text-nightBg`}
                                        >
                                            #{tag}
                                            <button
                                                type="button"
                                                onClick={() => removeEditTag(tag)}
                                                className="ml-1 text-white/80 hover:text-white"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Form Questions */}
                            {isLoadingEditFormQuestions ? (
                                <div className="space-y-4 border-t border-black/[0.06] pt-4 dark:border-white/10">
                                    <div className="flex items-center justify-center py-8">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary dark:border-nightAccent"></div>
                                            <p className="text-sm text-grey40 dark:text-nightMuted">Loading form questions...</p>
                                        </div>
                                    </div>
                                </div>
                            ) : editContactFormQuestions.length > 0 ? (
                                <div className="space-y-4 border-t border-black/[0.06] pt-4 dark:border-white/10">
                                    <h3 className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Additional Information</h3>
                                    {editContactFormQuestions.map((question) => (
                                        <div key={question.id} className={`${surface} p-5`}>
                                            {renderEditFormQuestion(question)}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </form>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onEditContactClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={accentBtnSm}
                            color="primary"
                            onPress={handleUpdateContact}
                            isDisabled={!editContact.name || !editContact.email || !!editEmailError || !validateEmail(editContact.email)}
                        >
                            Update Contact
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Export CSV Modal */}
            <Modal
                isOpen={isExportCsvOpen}
                onClose={onExportCsvClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Export Contacts to CSV
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div>
                                <label className={labelCls}>
                                    List <span className="text-red-500">*</span>
                                </label>
                                {selectedListView ? (
                                    <div className={`${nativeField} flex h-14 items-center`}>
                                        {selectedListView.name}
                                    </div>
                                ) : (
                                    <>
                                        <Select aria-label="Select" selectedKeys={[String(selectedListForExport ?? '')]} onChange={(e) => setSelectedListForExport(e.target.value)} isDisabled={isExportingCsv} isRequired>
                                            <SelectItem key="">Select a list to export</SelectItem>
                                            {contactLists.map((list: ContactList) => (
                                                <SelectItem key={String(list._id)}>
                                                    {list.name} ({list.contactCount} contact{list.contactCount !== 1 ? 's' : ''})
                                                </SelectItem>
                                            ))}
                                        </Select>
                                        {!selectedListForExport && (
                                            <p className="text-xs text-red-500 mt-1">Please select a list to export</p>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="text-sm text-grey40 dark:text-nightMuted">
                                The CSV file will contain all contacts from the selected list with the following fields: name, lastName, email, phone, country, city, and state.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onExportCsvClose}
                            isDisabled={isExportingCsv}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={accentBtnSm}
                            color="primary"
                            onPress={handleCsvExport}
                            isDisabled={(!selectedListForExport && !selectedListView?._id) || isExportingCsv}
                            isLoading={isExportingCsv}
                        >
                            {isExportingCsv ? 'Exporting...' : 'Export CSV'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* CSV Import Modal */}
            <Modal
                isOpen={isImportCsvOpen}
                onClose={onImportCsvClose}
                size={isMobile ? 'full' : 'lg'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Import Contacts from CSV
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div>
                                <label className={labelCls}>
                                    Select CSV File
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                    className={`${nativeField} file:mr-3 file:rounded-full file:border-0 file:bg-grey10 file:px-4 file:py-1.5 file:text-sm file:font-semibold file:text-white dark:file:bg-nightAccent dark:file:text-nightBg`}
                                />
                                <p className="mt-1 text-xs text-grey50 dark:text-nightMuted">
                                    CSV should have columns: name, lastname (or last_name), email, phone (or phone_number), country, city, state
                                </p>
                            </div>

                            {selectedListView ? (
                                <div>
                                    <label className={labelCls}>
                                        Add to List
                                    </label>
                                    <Input
                                        classNames={inputCls}
                                        readOnly
                                        value={selectedListView.name}
                                        className="w-full"
                                    />
                                    <p className="mt-1 text-xs text-grey50 dark:text-nightMuted">
                                        Imported contacts will be automatically added to: {selectedListView.name}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <label className={labelCls}>
                                        Add to List (Optional)
                                    </label>
                                    <Select aria-label="Select" selectedKeys={[String(csvImportListId ?? '')]} onChange={(e) => setCsvImportListId(e.target.value)}>
                                        <SelectItem key="">No list selected</SelectItem>
                                        {contactListsData?.data?.map((list: ContactList) => (
                                            <SelectItem key={String(list._id)}>
                                                {list.name}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                    <p className="mt-1 text-xs text-grey50 dark:text-nightMuted">
                                        Select a list to automatically add imported contacts to it
                                    </p>
                                </div>
                            )}

                            {csvImportError && (
                                <div className="rounded-2xl bg-red-50 p-4 ring-1 ring-red-200/60 dark:bg-red-500/10 dark:ring-red-500/20">
                                    <p className="text-red-800 dark:text-red-400 text-sm">{csvImportError}</p>
                                </div>
                            )}

                            {csvPreview.length > 0 && (
                                <div>
                                    <h4 className="mb-2 text-sm font-semibold text-grey20 dark:text-nightTextSecondary">
                                        Preview ({csvPreview.length} {csvPreview.length === 1 ? 'row' : 'rows'}):
                                    </h4>
                                    <div className="max-h-[400px] overflow-auto rounded-2xl ring-1 ring-black/[0.06] dark:ring-white/10">
                                        <table className="w-full text-xs">
                                            <thead className="sticky top-0 bg-grey99 dark:bg-nightElevated">
                                                <tr>
                                                    {Object.keys(csvPreview[0] || {}).map((header, index) => (
                                                        <th key={index} className="border-b border-black/[0.06] px-3 py-2 text-left text-xs font-bold uppercase tracking-[0.12em] text-grey50 dark:border-white/10 dark:text-nightMuted">
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-black/[0.06] dark:divide-white/10">
                                                {csvPreview.map((row, index) => (
                                                    <tr key={index} className={index % 2 === 0 ? '' : 'bg-grey99/60 dark:bg-white/[0.03]'}>
                                                        {Object.keys(csvPreview[0] || {}).map((header, cellIndex) => (
                                                            <td key={cellIndex} className="border-b border-black/[0.04] px-3 py-2 text-grey20 dark:border-white/[0.06] dark:text-nightTextSecondary">
                                                                {String(row[header] || '')}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="mt-2 text-xs text-grey50 dark:text-nightMuted">
                                        Showing first {csvPreview.length} {csvPreview.length === 1 ? 'row' : 'rows'} of your CSV file
                                    </p>
                                </div>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={onImportCsvClose}
                            className={`no-global-button-color ${toolBtn}`}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={`${accentBtnSm} px-6`}
                            onPress={handleCsvImport}
                            isDisabled={!csvFile || csvPreview.length === 0 || isCsvImporting}
                            isLoading={isCsvImporting}
                        >
                            {isCsvImporting ? 'Importing...' : 'Import Contacts'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Form Builder Modal */}


            {/* Form Submissions Modal */}
            {/*  */}

            {/* Delete List Confirmation Modal */}
            <Modal
                isOpen={isDeleteListConfirmOpen}
                onClose={onDeleteListConfirmClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Delete Contact List
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                                    <Trash width={24} height={24} className="text-red-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-grey10 dark:text-white">
                                        Are you sure you want to delete the list <span className="font-semibold">"{listToDelete?.name}"</span>?
                                    </p>
                                </div>
                            </div>
                            <div className="rounded-2xl bg-yellow-50 p-4 ring-1 ring-yellow-200/60 dark:bg-yellow-500/10 dark:ring-yellow-500/20">
                                <p className="text-sm text-yellow-800 dark:text-yellow-400">
                                    <strong>Warning:</strong> This action will remove all contacts from this list. The contacts themselves will not be deleted, but they will no longer be associated with this list.
                                </p>
                            </div>
                            <p className="text-sm text-grey40 dark:text-nightMuted">
                                This action cannot be undone.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onDeleteListConfirmClose}
                            isDisabled={deleteContactListMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="h-10 rounded-full px-5 text-sm font-semibold"
                            color="danger"
                            onPress={confirmDeleteList}
                            isLoading={deleteContactListMutation.isPending}
                            startContent={!deleteContactListMutation.isPending && <Trash width={16} height={16} />}
                        >
                            {deleteContactListMutation.isPending ? 'Deleting...' : 'Delete List'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Bulk Delete Contacts Confirmation Modal */}
            <Modal
                isOpen={isBulkDeleteConfirmOpen}
                onClose={onBulkDeleteConfirmClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Delete Selected Contacts
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                                    <Trash width={24} height={24} className="text-red-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-grey10 dark:text-white">
                                        Are you sure you want to delete <span className="font-semibold">{selectedContacts.length}</span> {selectedContacts.length === 1 ? 'contact' : 'contacts'}?
                                    </p>
                                </div>
                            </div>
                            <div className="rounded-2xl bg-yellow-50 p-4 ring-1 ring-yellow-200/60 dark:bg-yellow-500/10 dark:ring-yellow-500/20">
                                <p className="text-sm text-yellow-800 dark:text-yellow-400">
                                    <strong>Warning:</strong> This action will permanently delete {selectedContacts.length === 1 ? 'this contact' : 'these contacts'} from your contact list. This action cannot be undone.
                                </p>
                            </div>
                            <p className="text-sm text-grey40 dark:text-nightMuted">
                                This action cannot be undone.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onBulkDeleteConfirmClose}
                            isDisabled={bulkDeleteContactsMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="h-10 rounded-full px-5 text-sm font-semibold"
                            color="danger"
                            onPress={confirmBulkDelete}
                            isLoading={bulkDeleteContactsMutation.isPending}
                            startContent={!bulkDeleteContactsMutation.isPending && <Trash width={16} height={16} color="white" />}
                        >
                            {bulkDeleteContactsMutation.isPending ? 'Deleting...' : 'Delete Contacts'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Contact Confirmation Modal */}
            <Modal
                isOpen={isDeleteContactConfirmOpen}
                onClose={onDeleteContactConfirmClose}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Delete Contact
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                                    <Trash width={24} height={24} className="text-red-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-grey10 dark:text-white">
                                        Are you sure you want to delete <span className="font-semibold">"{contactToDelete?.name}"</span>?
                                    </p>
                                </div>
                            </div>
                            <div className="rounded-2xl bg-yellow-50 p-4 ring-1 ring-yellow-200/60 dark:bg-yellow-500/10 dark:ring-yellow-500/20">
                                <p className="text-sm text-yellow-800 dark:text-yellow-400">
                                    <strong>Warning:</strong> This action will permanently delete this contact from your contact list. This action cannot be undone.
                                </p>
                            </div>
                            <p className="text-sm text-grey40 dark:text-nightMuted">
                                This action cannot be undone.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onDeleteContactConfirmClose}
                            isDisabled={deleteContactMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="h-10 rounded-full px-5 text-sm font-semibold"
                            color="danger"
                            onPress={confirmDeleteContact}
                            isLoading={deleteContactMutation.isPending}
                            startContent={!deleteContactMutation.isPending && <Trash width={16} height={16} color="white" />}
                        >
                            {deleteContactMutation.isPending ? 'Deleting...' : 'Delete Contact'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Email Recipients Modal */}
            <Modal
                isOpen={isEmailRecipientsOpen}
                onClose={onEmailRecipientsClose}
                size={isMobile ? 'full' : '5xl'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div>
                            <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Email Recipients</h2>
                            {emailRecipientsData && (
                                <p className="mt-1 text-sm text-grey40 dark:text-nightMuted">
                                    {emailRecipientsData.subject} - {emailRecipientsData.recipients?.length || 0} recipients
                                </p>
                            )}
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        {isEmailRecipientsLoading ? (
                            <div className="text-center py-12">
                                <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary dark:border-nightAccent"></div>
                                <p className="mt-4 text-sm text-grey40 dark:text-nightMuted">Loading recipients...</p>
                            </div>
                        ) : emailRecipients.length === 0 ? (
                            <div className="rounded-[28px] bg-grey99 px-6 py-12 text-center text-grey40 dark:bg-white/[0.04] dark:text-nightMuted">
                                No recipients found for this email request.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex gap-4 text-sm flex-wrap">
                                        <span className="text-grey40 dark:text-nightMuted">
                                            Total: <span className="font-semibold text-grey10 dark:text-white">{emailRecipients.length}</span>
                                        </span>
                                        <span className="text-green-600 dark:text-green-400">
                                            Sent: <span className="font-semibold">{emailRecipients.filter((r: any) => r.status === 'sent').length}</span>
                                        </span>
                                        <span className="text-red-600 dark:text-red-400">
                                            Failed: <span className="font-semibold">{emailRecipients.filter((r: any) => r.status === 'failed').length}</span>
                                        </span>
                                        <span className="text-yellow-600 dark:text-yellow-400">
                                            Pending: <span className="font-semibold">{emailRecipients.filter((r: any) => r.status === 'pending').length}</span>
                                        </span>
                                    </div>
                                </div>
                                <div className={`${card} overflow-x-auto`}>
                                    <table className="min-w-full">
                                        <thead className="bg-grey99/70 dark:bg-white/[0.03]">
                                            <tr>
                                                <th className={thCls}>
                                                    Email
                                                </th>
                                                <th className={thCls}>
                                                    Name
                                                </th>
                                                <th className={thCls}>
                                                    Status
                                                </th>
                                                <th className={thCls}>
                                                    Sent At
                                                </th>
                                                <th className={thCls}>
                                                    Opened At
                                                </th>
                                                <th className={thCls}>
                                                    Viewed At
                                                </th>
                                                <th className={thCls}>
                                                    Error
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-black/[0.06] dark:divide-white/10">
                                            {emailRecipients.map((recipient: any) => (
                                                <tr key={recipient.id} className="transition-colors hover:bg-grey99 dark:hover:bg-white/[0.04]">
                                                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-grey10 dark:text-white">
                                                        {recipient.email}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-grey10 dark:text-white">
                                                        {recipient.name} {recipient.lastName}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <span
                                                            className={`${pill} capitalize ${recipient.status === 'sent'
                                                                ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                                                : recipient.status === 'failed'
                                                                    ? 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400'
                                                                    : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400'
                                                                }`}
                                                        >
                                                            {recipient.status}
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-grey40 dark:text-nightMuted">
                                                        {recipient.sentAt
                                                            ? new Date(recipient.sentAt).toLocaleString()
                                                            : '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-grey40 dark:text-nightMuted">
                                                        {recipient.openedAt
                                                            ? new Date(recipient.openedAt).toLocaleString()
                                                            : '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-grey40 dark:text-nightMuted">
                                                        {recipient.viewedAt
                                                            ? new Date(recipient.viewedAt).toLocaleString()
                                                            : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400">
                                                        {recipient.errorMessage || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onEmailRecipientsClose}
                        >
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Email History Preview Modal */}
            <Modal
                isOpen={isEmailHistoryPreviewOpen}
                onClose={onEmailHistoryPreviewClose}
                size={isMobile ? 'full' : '5xl'}
                scrollBehavior="inside"
                classNames={{
                    ...modalCls,
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Email Preview - {selectedEmailForPreview?.subject || 'Email'}
                    </ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <div className="mb-2 text-sm text-grey40 dark:text-nightMuted">
                                <div><strong>Subject:</strong> {selectedEmailForPreview?.subject || '(No subject)'}</div>
                                <div className="mt-1"><strong>Sent:</strong> {selectedEmailForPreview?.sentCount || 0} / {selectedEmailForPreview?.totalRecipients || 0} recipients</div>
                                <div className="mt-1"><strong>Status:</strong> <span className={`${pill} capitalize ${selectedEmailForPreview?.status === 'completed'
                                    ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                    : selectedEmailForPreview?.status === 'processing'
                                        ? 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent'
                                        : selectedEmailForPreview?.status === 'failed'
                                            ? 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400'
                                            : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400'
                                    }`}>{selectedEmailForPreview?.status || 'unknown'}</span></div>
                            </div>
                            <div className="overflow-hidden rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/10">
                                <iframe
                                    srcDoc={selectedEmailForPreview ? generateEmailHistoryPreview(selectedEmailForPreview) : ''}
                                    className="w-full h-[600px] border-0"
                                    title="Email History Preview"
                                />
                            </div>
                            <div className="text-xs italic text-grey50 dark:text-nightMuted">
                                This is how the email appeared to recipients. Note: Some email clients may render styles differently.
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className={toolBtn}
                            variant="light"
                            onPress={onEmailHistoryPreviewClose}
                        >
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};

export default ContactListTab;

